<template>
    <div class="detalii-wrapper" :class="{ 'detalii-fullscreen': isFullscreen }">

        <!-- ── Header Card ── -->
        <div v-if="localOrderData" class="order-header-card">
            <div class="order-header-top">
                <div class="order-header-main">
                    <i class="dx-icon dx-icon-doc header-icon" />
                    <div class="order-header-texts">
                        <span class="order-cod">{{ localOrderData.Cod }}</span>
                    </div>
                    <span v-if="localOrderData.Proiect?.Cod" class="po-chip">{{ localOrderData.Proiect.Cod }}</span>
                    <span class="stare-chip" :style="getStareChipStyle(localOrderData.Stare)">{{ getStareLabel(localOrderData.Stare) }}</span>
                    <span v-if="localOrderData.Facturat" class="facturat-chip">Facturat</span>
                    <span v-if="localOrderData.LivrareDirectaFurnizor" class="dropship-chip" title="Furnizorul livrează direct la client, fără să treacă prin gestiunea proprie">Livrare directă furnizor</span>
                </div>
                <div class="header-actions">
                    <DxButton v-for="btn in visibleStateButtons" :key="btn.stareNoua" :text="btn.text" :type="btn.type"
                        :hint="btn.disabledHint" styling-mode="outlined"
                        :disabled="editingByUser !== null || btn.disabled" @click="onStateButtonClick(btn)" />
                    <DxButton v-if="!isReadOnly && drepturi.EditeazaComenzi" text="Editează comandă" icon="edit" styling-mode="text"
                        :disabled="editingByUser !== null || localOrderData?.Stare === 'InLucru'" @click="onEditHeader" />
                </div>
            </div>

            <div class="order-meta">
                <div class="meta-item meta-item--partener"
                     @mouseenter="showMetaTooltip($event, 'Partener', [localOrderData.Client?.Nume, localOrderData.Client?.CUI].filter(Boolean).join('\n'))"
                     @mouseleave="hideMetaTooltip">
                    <i class="dx-icon dx-icon-group meta-icon" />
                    <div class="meta-content">
                        <span class="meta-label">Partener</span>
                        <span class="meta-value meta-value--partener">{{ localOrderData.Client?.Nume || '—' }}</span>
                        <span v-if="localOrderData.Client?.CUI" class="meta-sub">{{ localOrderData.Client.CUI }}</span>
                    </div>
                </div>
                <div class="meta-divider" />
                <div class="meta-item">
                    <i class="dx-icon dx-icon-event meta-icon" />
                    <div class="meta-content">
                        <span class="meta-label">Data</span>
                        <span class="meta-value">{{ formatDate(localOrderData.DataComenzii) }}</span>
                    </div>
                </div>
                <template v-if="localOrderData.TermenLivrare">
                    <div class="meta-divider" />
                    <div class="meta-item">
                        <i class="dx-icon dx-icon-clock meta-icon" />
                        <div class="meta-content">
                            <span class="meta-label">Termen Livrare</span>
                            <span class="meta-value">{{ formatDate(localOrderData.TermenLivrare) }}</span>
                        </div>
                    </div>
                </template>
                <template v-if="localOrderData.Agent?.Nume">
                    <div class="meta-divider" />
                    <div class="meta-item">
                        <i class="dx-icon dx-icon-user meta-icon" />
                        <div class="meta-content">
                            <span class="meta-label">Agent</span>
                            <span class="meta-value">{{ localOrderData.Agent?.Nume }}</span>
                        </div>
                    </div>
                </template>
                <template v-if="localOrderData.Observatii">
                    <div class="meta-divider" />
                    <div class="meta-item meta-item--obs"
                         @mouseenter="showMetaTooltip($event, 'Observații', localOrderData.Observatii)"
                         @mouseleave="hideMetaTooltip">
                        <i class="dx-icon dx-icon-comment meta-icon" />
                        <div class="meta-content">
                            <span class="meta-label">Observații</span>
                            <span class="meta-value meta-value--obs">{{ localOrderData.Observatii }}</span>
                        </div>
                    </div>
                </template>
                <template v-if="tipComandaNume">
                    <div class="meta-divider" />
                    <div class="meta-item">
                        <i class="dx-icon dx-icon-tags meta-icon" />
                        <div class="meta-content">
                            <span class="meta-label">Tip comandă</span>
                            <span class="meta-value">{{ tipComandaNume }}</span>
                        </div>
                    </div>
                </template>
                <div class="meta-spacer" />
                <div class="meta-summary-zone">
                    <template v-if="cursEUR !== null || cursUSD !== null">
                        <div class="meta-cursuri-box">
                            <span class="meta-label">Cursuri BNR</span>
                            <div class="cursuri-rows">
                                <div v-if="cursEUR !== null" class="curs-row">
                                    <span class="curs-simbol">EUR</span>
                                    <span class="curs-valoare">{{ formatRO(cursEUR) }}</span>
                                </div>
                                <div v-if="cursUSD !== null" class="curs-row">
                                    <span class="curs-simbol">USD</span>
                                    <span class="curs-valoare">{{ formatRO(cursUSD) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="meta-divider" style="margin: 0 10px;" />
                    </template>
                    <div class="meta-totals-box">
                        <div class="totals-row">
                            <span class="totals-label">Fără TVA</span>
                            <span class="totals-value-secondary">{{ formatMoney2(localOrderData.ValoareComanda) }}&nbsp;{{ localOrderData.Valuta?.Simbol || '' }}</span>
                        </div>
                        <div class="totals-separator" />
                        <div class="totals-row">
                            <span class="totals-label totals-label--main">
                                <i class="dx-icon dx-icon-money" style="font-size:11px;vertical-align:middle;margin-right:3px" />
                                Total cu TVA
                            </span>
                            <span class="totals-value-main">{{ formatMoney2(localOrderData.TotalComanda) }}&nbsp;{{ localOrderData.Valuta?.Simbol || '' }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- ── Taburi documente (bar compact orizontal) ── -->
        <div v-if="!isFullscreen" class="docs-tabs">
            <div class="docs-tab-bar">
                <button v-if="localOrderData && documentsGenerate.length > 0"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'docgen' }"
                    @click="toggleDocTab('docgen')">
                    <i class="dx-icon dx-icon-doc" />
                    <span>Documente</span>
                    <span class="tab-badge">{{ documentsGenerate.length }}</span>
                </button>
                <button v-if="localOrderData && (rezervari.length > 0 || currentCategorie?.RezervareDocID)"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'rezervari' }"
                    @click="toggleDocTab('rezervari')">
                    <i class="dx-icon dx-icon-clock" />
                    <span>Rezervări</span>
                    <span v-if="rezervari.length > 0" class="tab-badge">{{ rezervari.length }}</span>
                </button>
                <button v-if="localOrderData && (avize.length > 0 || ['InLucru','Finalizata'].includes(localOrderData.Stare ?? ''))"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'avize' }"
                    @click="toggleDocTab('avize')">
                    <i class="dx-icon dx-icon-export" />
                    <span>Documente Gestiune</span>
                    <span v-if="avize.length > 0" class="tab-badge">{{ avize.length }}</span>
                </button>
                <button v-if="localOrderData && isClientOrder && (facturi.length > 0 || ['InLucru','Finalizata'].includes(localOrderData.Stare ?? ''))"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'facturi' }"
                    @click="toggleDocTab('facturi')">
                    <i class="dx-icon dx-icon-money" />
                    <span>Facturi</span>
                    <span v-if="facturi.length > 0" class="tab-badge">{{ facturi.length }}</span>
                </button>
                <button v-if="localOrderData && (receptii.length > 0 || (!isClientOrder && ['Confirmata','InLucru'].includes(localOrderData.Stare ?? '')))"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'receptii' }"
                    @click="toggleDocTab('receptii')">
                    <i class="dx-icon dx-icon-import" />
                    <span>Recepții</span>
                    <span v-if="receptii.length > 0" class="tab-badge">{{ receptii.length }}</span>
                </button>
                <button v-if="localOrderData && comenziStorno.length > 0"
                    class="docs-tab-chip" :class="{ active: activeDocTab === 'storno' }"
                    @click="toggleDocTab('storno')">
                    <i class="dx-icon dx-icon-revert" />
                    <span>Storno</span>
                    <span class="tab-badge">{{ comenziStorno.length }}</span>
                </button>
                <div class="docs-tab-actions">
                    <span v-if="drepturi.ReceptioneazaMarfa && localOrderData && !isClientOrder && ['Confirmata','InLucru'].includes(localOrderData.Stare ?? '')" @click.stop>
                        <DxButton text="Recepționează" icon="plus" type="default" styling-mode="outlined" size="small"
                            :disabled="editingByUser !== null || isGenereazaReceptie"
                            @click="onOpenReceptiePopup" />
                    </span>
                </div>
            </div>

            <div v-if="activeDocTab" class="docs-tab-panel">
                <!-- Documente generate -->
                <div v-if="activeDocTab === 'docgen'">
                    <DxDataGrid :data-source="documentsGenerate" key-expr="DocumentID" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :allow-column-resizing="true" :column-resizing-mode="'widget'"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn caption="" :width="60" :fixed="true" fixed-position="left" :allow-sorting="false" :allow-resizing="false" cell-template="actiuniDocTemplate" />
                        <DxColumn data-field="TipCod" caption="Tip" :width="80" :allow-resizing="true" />
                        <DxColumn data-field="TipNume" caption="Denumire tip" :allow-resizing="true" />
                        <DxColumn data-field="Numar" caption="Număr" :width="140" :allow-resizing="true" />
                        <template #actiuniDocTemplate="{ data }">
                            <DxButton icon="eyeopen" styling-mode="text" hint="Deschide document"
                                @click="onOpenDocument(data.data)" />
                        </template>
                    </DxDataGrid>
                </div>
                <!-- Rezervări (documente hold, temporare) -->
                <div v-if="activeDocTab === 'rezervari'">
                    <DxDataGrid :data-source="rezervari" key-expr="Id" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn data-field="Tip.Nume" caption="Tip" />
                        <DxColumn data-field="Numar" caption="Număr" :width="140" />
                        <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn caption="Anulat" :width="100" alignment="center" :allow-sorting="false" cell-template="stareRezervareTemplate" />
                        <DxColumn caption="" :width="70" :allow-sorting="false" cell-template="deschideRezervareTemplate" />
                        <template #stareRezervareTemplate="{ data }">
                            <span v-if="data.data.Anulat" class="doc-anulat-chip">Anulată</span>
                        </template>
                        <template #deschideRezervareTemplate="{ data }">
                            <DxButton icon="eyeopen" styling-mode="text" hint="Deschide documentul"
                                @click="onOpenDocument({ DocumentID: data.data.Id })" />
                        </template>
                    </DxDataGrid>
                </div>
                <!-- Avize expediție -->
                <div v-if="activeDocTab === 'avize'">
                    <DxDataGrid :data-source="avize" key-expr="Id" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn data-field="Tip.Nume" caption="Tip" />
                        <DxColumn data-field="Numar" caption="Număr" :width="140" />
                        <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn data-field="ExpediereNume" caption="Delegat" />
                        <DxColumn data-field="ExpediereMijloc" caption="Mijloc transport" />
                        <DxColumn data-field="Observatii" caption="Observații" />
                        <DxColumn caption="Stare" :width="90" alignment="center" :allow-sorting="false" cell-template="stareAvizTemplate" />
                        <DxColumn caption="" :width="150" :allow-sorting="false" cell-template="actiuniAvizTemplate" />
                        <template #stareAvizTemplate="{ data }">
                            <span v-if="data.data.Anulat" class="doc-anulat-chip">Anulat</span>
                        </template>
                        <template #actiuniAvizTemplate="{ data }">
                            <div style="display:flex;gap:2px">
                                <DxButton icon="eyeopen" styling-mode="text" hint="Deschide documentul (cu liniile comenzii)"
                                    @click="onOpenDocument({ DocumentID: data.data.Id })" />
                                <DxButton icon="print" styling-mode="text" hint="Tipărește aviz"
                                    @click="onTiparesteAviz(data.data)" />
                                <DxButton icon="remove" styling-mode="text"
                                    :hint="!drepturi.AnuleazaAvize ? 'Nu aveți dreptul de a anula avize.' : 'Anulează aviz'" type="danger"
                                    :disabled="data.data.Anulat || !drepturi.AnuleazaAvize"
                                    @click="onAnuleazaAviz(data.data)" />
                            </div>
                        </template>
                    </DxDataGrid>
                </div>
                <!-- Facturi -->
                <div v-if="activeDocTab === 'facturi'">
                    <DxDataGrid :data-source="facturi" key-expr="Id" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn data-field="Tip.Nume" caption="Tip" />
                        <DxColumn data-field="Numar" caption="Număr" :width="140" />
                        <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn data-field="Scadenta" caption="Scadență" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn data-field="Valoare" caption="Val. fără TVA" data-type="number" :format="formatRO" :width="140" alignment="right" />
                        <DxColumn data-field="Total" caption="Total" data-type="number" :format="formatRO" :width="120" alignment="right" />
                        <DxColumn data-field="Observatii" caption="Observații" />
                        <DxColumn caption="Stare" :width="100" alignment="center" :allow-sorting="false" cell-template="stareFacturaTemplate" />
                        <DxColumn caption="" :width="260" :allow-sorting="false" cell-template="actiuniFacturaTemplate" />
                        <template #stareFacturaTemplate="{ data }">
                            <span v-if="data.data.Anulat" class="doc-anulat-chip">Anulată</span>
                            <span v-else-if="data.data.StornatRetur" class="doc-stornat-chip">Stornată</span>
                        </template>
                        <template #actiuniFacturaTemplate="{ data }">
                            <div style="display:flex;gap:2px">
                                <DxButton icon="eyeopen" styling-mode="text" hint="Deschide documentul (cu liniile comenzii)"
                                    @click="onOpenDocument({ DocumentID: data.data.Id })" />
                                <DxButton icon="print" styling-mode="text" hint="Tipărește factură"
                                    @click="onTiparesteFactura(data.data)" />
                                <DxButton icon="checkmarkcircle" styling-mode="text" hint="Tipărește certificat de garanție"
                                    @click="onTipareasteCertificatGarantie(data.data)" />
                                <DxButton v-if="drepturi.GenereazaFacturi" icon="minus" styling-mode="text" hint="Stornează factură" type="danger"
                                    :disabled="data.data.StornatRetur || data.data.Anulat"
                                    @click="onStorneazaFactura(data.data)" />
                                <DxButton v-if="drepturi.AnuleazaFacturi" icon="close" styling-mode="text" hint="Anulează factură (fără document de corecție)" type="danger"
                                    :disabled="data.data.StornatRetur || data.data.Anulat"
                                    @click="onAnuleazaFactura(data.data)" />
                            </div>
                        </template>
                    </DxDataGrid>
                </div>
                <!-- Recepții (NIR) -->
                <div v-if="activeDocTab === 'receptii'">
                    <DxDataGrid :data-source="receptii" key-expr="Id" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn data-field="Numar" caption="Nr. NIR" :width="130" />
                        <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn data-field="Valoare" caption="Val. fără TVA" data-type="number" :format="formatRO" :width="140" alignment="right" />
                        <DxColumn data-field="Total" caption="Total" data-type="number" :format="formatRO" :width="120" alignment="right" />
                        <DxColumn data-field="Observatii" caption="Observații" />
                        <DxColumn caption="" :width="90" :allow-sorting="false" cell-template="printNirTemplate" />
                        <template #printNirTemplate="{ data }">
                            <div style="display:flex;gap:2px">
                                <DxButton icon="eyeopen" styling-mode="text" hint="Deschide documentul (cu liniile comenzii)"
                                    @click="onOpenDocument({ DocumentID: data.data.Id })" />
                                <DxButton icon="print" styling-mode="text" hint="Tipărește NIR"
                                    @click="onTiparesteNir(data.data)" />
                            </div>
                        </template>
                    </DxDataGrid>
                </div>
                <!-- Comenzi Storno -->
                <div v-if="activeDocTab === 'storno'">
                    <DxDataGrid :data-source="comenziStorno" key-expr="Id" :show-borders="false"
                        :show-column-lines="false" :show-row-lines="true"
                        :hover-state-enabled="true" :row-alternation-enabled="true"
                        class="docs-panel-grid">
                        <DxColumn data-field="Cod" caption="Cod" :width="140" />
                        <DxColumn data-field="DataComenzii" caption="Data" data-type="date" format="dd.MM.yyyy" :width="110" alignment="center" />
                        <DxColumn data-field="Stare" caption="Stare" :width="120" cell-template="stareStornoTemplate" />
                        <DxColumn data-field="TotalComanda" caption="Total" data-type="number" :format="formatRO" :width="130" alignment="right" cell-template="totalStornoTemplate" />
                        <DxColumn caption="" :width="70" :allow-sorting="false" cell-template="deschideStornoTemplate" />
                        <template #stareStornoTemplate="{ data }">
                            <span class="stare-chip" :style="getStareChipStyle(data.value)">{{ getStareLabel(data.value) }}</span>
                        </template>
                        <template #totalStornoTemplate="{ data }">
                            <span class="total-cell">{{ formatMoney(data.value) }}</span>
                        </template>
                        <template #deschideStornoTemplate="{ data }">
                            <DxButton icon="eyeopen" styling-mode="text" hint="Deschide comanda storno"
                                @click="emit('open-order', data.data)" />
                        </template>
                    </DxDataGrid>
                </div>
            </div>
        </div>

        <!-- ── Banner editing concurent ── -->
        <div v-if="editingByUser" class="editing-banner">
            <i class="dx-icon dx-icon-warning editing-banner-icon" />
            <span><b>{{ editingByUser }}</b> editează această comandă în acest moment.</span>
        </div>

        <!-- ── Loading ── -->
        <div v-else-if="isLoading" class="loading-placeholder">
            <span class="loading-text">Se încarcă...</span>
        </div>

        <!-- ── Loading tipărire PDF ── -->
        <DxLoadPanel :visible="isPrinting" message="Se generează PDF-ul..." :show-overlay="true" :show-pane="true" />

        <!-- ── Grid Detalii ── -->
        <DxDataGrid ref="gridRef" :data-source="detaliiStore" key-expr="ID" show-borders :column-auto-width="false"
            :column-min-width="70"
            :allow-column-resizing="true" :column-resizing-mode="'nextColumn'" :allow-column-reordering="true"
            :focused-row-enabled="true"
            :show-column-lines="true" :show-row-lines="true"
            :column-chooser="{ enabled: true, mode: 'select', allowSearch: true }" :filter-row="{ visible: true }"
            :paging="{ enabled: false }" :pager="{ visible: false }" :row-alternation-enabled="true" :hover-state-enabled="true" height="100%"
            class="detalii-grid" @row-inserting="onRowInserting" @row-updating="onRowUpdating"
            @row-removing="onRowRemoving" @key-down="onGridKeyDown" @content-ready="onGridContentReady"
            @selection-changed="onSelectionChanged" @editor-preparing="onEditorPreparing"
            @context-menu-preparing="onDetaliiContextMenuPreparing" @initialized="onGridInitialized"
            @cell-prepared="onDetaliiCellPrepared" @row-prepared="onDetaliiRowPrepared">
            <DxStateStoring :enabled="true" type="localStorage" storage-key="grid-detalii-comenzi" />
            <DxSelection mode="multiple" show-check-boxes-mode="always" />
            <DxEditing :allow-updating="allowUpdatingFn" :allow-adding="!liniiReadOnly" :allow-deleting="false"
                mode="batch" :use-icons="true" :confirm-delete="false" new-row-position="last" />
            <DxColumn type="buttons" :fixed="true" fixed-position="right" :visible="false" />

            <DxToolbar>
                <DxItem location="before" template="fullscreenBtn" />
                <DxItem location="before" template="salveazaTotBtn" />
                <DxItem location="before" template="adaugaProdusBtn" />
                <DxItem v-if="drepturi.RezervaComenzi && !isReadOnly && !isComandaStorno && currentCategorie?.RezervareDocID && localOrderData?.Stare === 'Confirmata' && existaLiniiActive" location="before" template="rezervaToateBtn" />
                <DxItem v-if="drepturi.ModificaPretComenzi && !isReadOnly && refreshCursNecesar" location="before" template="refreshCursBtn" />
                <!-- Ascuns temporar (2026-07-13): fluxul generic (DOC_DocumenteRepository.GenereazaDocumentDinComanda)
                     nu are aceleași blocaje de business ca "Generează Factură"/"Generează Aviz" (ex. fără guard
                     TipFurnizor pe facturare, fără precondiția "recepționat prin NIR retur" la storno) — se dorește
                     dezactivat până se clarifică regulile de business. Cod rămas intact, doar ascuns din UI. -->
                <DxItem v-if="false && localOrderData?.Stare !== 'Anulata'" location="before" template="genereazaDocBtn" />
                <DxItem v-if="drepturi.GenereazaAvize && ['InLucru','Finalizata'].includes(localOrderData?.Stare ?? '')" location="before" template="genereazaAvizBtn" />
                <DxItem v-if="drepturi.GenereazaFacturi && isClientOrder && localOrderData?.Stare === 'Confirmata' && toateRezervate && !localOrderData?.Facturat" location="before" template="genereazaFacturaBtn" />
                <DxItem v-if="drepturi.DuplicaComenzi && existaFurnizoriPeLinii" location="before" template="genereazaComenziFurnizoriBtn" />
                <DxItem v-if="drepturi.DuplicaComenzi && liniiSelectate.length > 0" location="before" template="stornoPartialBtn" />
                <DxItem v-if="!liniiReadOnly && liniiSelectate.length > 0" location="before" template="stergeSelectateBtn" />
                <DxItem name="columnChooserButton" />
            </DxToolbar>
            <template #fullscreenBtn>
                <DxButton :icon="isFullscreen ? 'collapse' : 'expand'" styling-mode="text"
                    :hint="isFullscreen ? 'Ieși din ecran complet' : 'Ecran complet'"
                    @click="toggleFullscreen" />
            </template>
            <template #adaugaProdusBtn>
                <DxButton text="Adaugă produs în comandă" icon="plus" type="default" styling-mode="outlined"
                    :hint="isComandaStorno ? 'Comenzile storno se generează automat din comanda sursă — nu se pot adăuga produse noi.' : undefined"
                    :disabled="liniiReadOnly || isComandaStorno" @click="gridRef?.instance?.addRow()" />
            </template>
            <template #salveazaTotBtn>
                <DxButton text="Salvează tot" icon="save" type="success" styling-mode="contained"
                    :disabled="liniiReadOnly" @click="onSalveazaTot" />
            </template>

            <template #rezervaToateBtn>
                <DxButton text="Rezervă toate" icon="check" type="default" styling-mode="outlined"
                    :disabled="isRezervandToate || editingByUser !== null" @click="onRezervaToate" />
            </template>
            <template #genereazaDocBtn>
                <DxButton text="Generează document" icon="doc" type="default" styling-mode="outlined"
                    :hint="genereazaDocDisabledHint"
                    :disabled="editingByUser !== null || !!genereazaDocDisabledHint"
                    @click="onOpenGenereazaDocPopup" />
            </template>
            <template #genereazaAvizBtn>
                <DxButton text="Generează Documente Gestiune" icon="plus" type="default" styling-mode="outlined"
                    :hint="existaAvizActiv ? 'Există deja un aviz activ pentru această comandă. Anulați-l pentru a genera altul.' : undefined"
                    :disabled="editingByUser !== null || isGenereazaAviz || existaAvizActiv"
                    @click="onOpenGenereazaAvizPopup" />
            </template>
            <template #genereazaFacturaBtn>
                <DxButton text="Generează Factură" icon="money" type="default" styling-mode="outlined"
                    :disabled="editingByUser !== null || isGenereazaFactura"
                    @click="onOpenGenereazaFacturaPopup" />
            </template>
            <template #genereazaComenziFurnizoriBtn>
                <DxButton text="Generează comenzi furnizori" icon="add" type="default" styling-mode="outlined"
                    :hint="!localOrderData?.ProiectId ? 'Comanda trebuie să aibă un Proiect setat.' : undefined"
                    :disabled="!localOrderData?.ProiectId || isGenerandComenziFurnizori"
                    @click="onGenereazaComenziFurnizori" />
            </template>
            <template #refreshCursBtn>
                <DxButton text="Refresh curs" icon="refresh" type="normal" styling-mode="outlined"
                    :disabled="isRefreshingCurs || localOrderData?.Stare === 'InLucru'" @click="onRefreshCursClick" />
            </template>
            <template #stornoPartialBtn>
                <DxButton :text="`Storno parțial (${liniiSelectate.length})`" icon="revert" type="normal"
                    styling-mode="outlined"
                    :hint="!localOrderData?.Facturat ? 'Nu se poate genera storno pentru o comandă fără factură activă generată.' : undefined"
                    :disabled="isGenerandStorno || !localOrderData?.Facturat" @click="onGenereazaStornoPartial" />
            </template>
            <template #stergeSelectateBtn>
                <DxButton :text="`Șterge (${liniiSelectate.length})`" icon="trash" type="danger"
                    styling-mode="text" @click="onStergeLiniiSelectate" />
            </template>

            <DxColumn data-field="NrCrt" caption="Numar curent" hint="Numarul de ordine al liniei in comanda" data-type="number" width="55" alignment="center" sort-order="asc" :sort-index="0" />
            <DxColumn data-field="ProdusID" caption="Produs" hint="Produsul sau serviciul din linia de comanda" :min-width="200" data-type="string" :calculate-display-value="getProdusNume"
                :calculate-filter-expression="produsFilterExpression"
                :filter-operations="['contains', 'notcontains', 'startswith', 'endswith']"
                selected-filter-operation="contains"
                edit-cell-template="produsEditor">
                <DxRequiredRule message="Produsul este obligatoriu." />
            </DxColumn>
            <DxColumn data-field="Produs.Cod" caption="Cod" hint="Codul de catalog al produsului" width="110" :allow-editing="false" />
            <DxColumn data-field="Produs.CodIntern" caption="Cod Intern" hint="Codul intern de gestiune al produsului" width="110" :allow-editing="false" :visible="false" />
            <DxColumn data-field="Cantitate" caption="Cantitate" hint="Numărul de unități (bucăți) dintr-un anumit produs care au fost adăugate pe acea linie de comandă." data-type="number" width="80" alignment="right" />
            <DxColumn data-field="CantitateRezervata" caption="Cantitate Rezervată" hint="Cantitate totală / Cantitate rezervată din stoc" :width="130" :allow-editing="false"
                alignment="center" cell-template="rezervareCellTemplate" />
            <DxColumn data-field="PretLista" caption="Pret Lista" hint="Prețul standard de catalog sau de bază al unui singur produs, stabilit de obicei în valută (fără discount și fără TVA)." data-type="number" :format="formatFixed2" width="115"
                alignment="right" />
            <DxColumn data-field="PretListaValutaID" caption="Valuta Pret Lista" hint="Valuta in care este exprimat pretul de lista" width="90" edit-cell-template="valutaListaEditor">
                <DxLookup :data-source="dsValuteLookup" display-expr="Simbol" value-expr="Id" />
            </DxColumn>
            <DxColumn data-field="PretListaCurs" caption="Curs Pret Lista" hint="Cursul de schimb valutar utilizat de sistem pentru a converti prețul produsului din valută în moneda națională (RON)." data-type="number" :format="formatFixed2" width="110"
                alignment="right" :visible="false" :allow-editing="false" :calculate-cell-value="calcCursLista" />
            <DxColumn data-field="PretUnitar" caption="Pret Unitar" hint="Prețul de bază al unei singure bucăți, în moneda comenzii, înainte de aplicarea discounturilor sau a taxelor. Este câmpul editabil din care se derivă Pret cu Discount." data-type="number" :format="formatFixed2" width="115"
                alignment="right" cell-template="pretUnitarCellTemplate" />
            <DxColumn caption="Valoare fara Discount" hint="Costul cumulat al produselor de pe acea linie la preț întreg, calculat prin înmulțirea prețului unitar cu cantitatea (fără discount și fără TVA)." data-type="number" :format="formatFixed2" width="160" alignment="right"
                :allow-editing="false" cell-template="valFaraDiscCellTemplate" :visible="false" />
            <DxColumn data-field="DiscountProc" caption="Discount %" hint="Procentul de reducere comercială (%) acordat clientului pentru produsul respectiv." data-type="number" width="90" alignment="center"
                cell-template="discountCellTemplate" />
            <DxColumn data-field="ValoareDiscount" caption="Reducere Aplicată" hint="Suma totală în bani (RON) care se scade din valoarea produselor de pe acea linie ca urmare a aplicării procentului de discount." data-type="number" :format="formatFixed2"
                width="160" alignment="right" :allow-editing="false" :visible="false" cell-template="valDiscountCellTemplate" />
            <DxColumn data-field="PretNegociat" caption="Pret cu Discount" hint="Prețul final stabilit de comun acord cu clientul — se calculează automat din Pret Unitar și Discount %, nu se editează direct." data-type="number" :format="formatFixed2" width="130"
                alignment="right" cell-template="pretNegociatCellTemplate" :allow-editing="false" />
            <DxColumn caption="Stoc Efectiv" hint="Stocul fizic disponibil al produsului in gestiune" data-type="number" :format="formatFixed2" width="115" alignment="right"
                :allow-editing="false" :calculate-cell-value="calcStocEfectiv" :visible="false" />
            <DxColumn caption="Stoc Rezervat" hint="Cantitatea rezervata din stoc pentru alte comenzi" data-type="number" :format="formatFixed2" width="115" alignment="right"
                :allow-editing="false" :calculate-cell-value="calcStocRezervat" :visible="false" />
            <DxColumn caption="Stoc Ramas" hint="Stocul ramas disponibil dupa scaderea rezervarilor (Stoc Efectiv - Stoc Rezervat)" data-type="number" :format="formatFixed2" width="110" alignment="right"
                :allow-editing="false" :calculate-cell-value="calcStocRamas" :visible="false" />
            <DxColumn data-field="TVA" caption="TVA %" hint="Procentul de TVA aplicat pe aceasta linie" data-type="number" width="75" alignment="center" />
            <DxColumn data-field="Valoare" caption="Valoare cu Discount" hint="Baza de impozitare pentru linia respectivă; valoarea netă totală a produselor după ce s-au aplicat discounturile sau modificările de preț, dar înainte de adăugarea taxelor." data-type="number" :format="formatFixed2" width="150"
                alignment="right" :allow-editing="false" cell-template="valCuDiscCellTemplate" />
            <DxColumn data-field="ValoareTVA" caption="Valoare TVA" hint="Suma Taxei pe Valoarea Adăugată calculată strict pentru produsele de pe acea linie (prin aplicarea cotei de TVA la valoarea fără TVA)." data-type="number" :format="formatFixed2" width="110"
                alignment="right" :allow-editing="false" :visible="false" cell-template="valTVACellTemplate" />
            <DxColumn data-field="TotalProdus" caption="Total cu TVA" hint="Valoarea totală brută (cu discounturi aplicate și TVA inclus) pentru cantitatea totală din produsul respectiv. Reprezintă costul final al acelei linii din comandă." data-type="number" :format="formatFixed2" width="130"
                alignment="right" :allow-editing="false" cell-template="totalCellTemplate" />

            <template #rezervareCellTemplate="{ data }">
                <div class="rezervare-cell">
                    <span v-if="data?.data?.Produs?.Stocabil === false" class="rezervare-text rezervare-none">—</span>
                    <template v-else>
                        <span class="rezervare-text" :class="getRezervareClass(data?.data?.ID, data?.data?.Cantitate)">
                            {{ formatRezervare(data?.data?.ID, data?.data?.Cantitate) }}
                        </span>
                        <i v-if="data?.data?.ProdusID && !isComandaStorno && currentCategorie?.RezervareDocID"
                            class="dx-icon dx-icon-edit edit-rezervare-btn"
                            :class="{ 'edit-rezervare-btn--disabled': liniiReadOnly }"
                            :title="liniiReadOnly ? 'Comanda nu mai poate fi modificată în starea curentă.' : 'Modifică rezervare'"
                            @click="(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); if (!liniiReadOnly) onOpenRezerveLinie(data?.data); }"></i>
                    </template>
                </div>
            </template>

            <template #pretUnitarCellTemplate="{ data }">
                <span>{{ formatFixed2(getPretUnitarDisplay(data)) }}</span>
            </template>
            <template #pretNegociatCellTemplate="{ data }">
                <span>{{ formatFixed2(getPretNegociatDisplay(data)) }}</span>
            </template>
            <template #valFaraDiscCellTemplate="{ data }">
                <span class="cell-val-fara-disc">{{ formatFixed2(calcCache[data.key]?.Valoare ?? data.data.Valoare) }}</span>
            </template>
            <template #valCuDiscCellTemplate="{ data }">
                <span class="cell-val-cu-disc">{{ formatFixed2(calcCache[data.key]?.ValoareFaraTVA ?? data.data.ValoareFaraTVA) }}</span>
            </template>
            <template #valDiscountCellTemplate="{ data }">
                <span>{{ formatFixed2(calcCache[data.key]?.ValoareDiscount ?? data.data.ValoareDiscount) }}</span>
            </template>
            <template #valTVACellTemplate="{ data }">
                <span>{{ formatFixed2(calcCache[data.key]?.ValoareTVA ?? data.data.ValoareTVA) }}</span>
            </template>
            <template #valFaraTVACellTemplate="{ data }">
                <span>{{ formatFixed2(calcCache[data.key]?.ValoareFaraTVA ?? data.data.ValoareFaraTVA) }}</span>
            </template>
            <template #totalCellTemplate="{ data }">
                <span class="total-cell">{{ formatFixed2(calcCache[data.key]?.TotalProdus ?? data.data.TotalProdus) }}</span>
            </template>
            <template #discountCellTemplate="{ data }">
                <span v-if="data.value" class="discount-badge">{{ data.value }}%</span>
                <span v-else class="no-discount">—</span>
            </template>

            <template #produsEditor="{ data: cellInfo }">
                <DxDropDownBox
                    :value="cellInfo.value"
                    :data-source="dsProduse"
                    :display-expr="getProdusLookupDisplay"
                    value-expr="Id"
                    :show-clear-button="true"
                    :drop-down-options="{ width: 700, height: 450, minWidth: 400, minHeight: 300, resizeEnabled: true }"
                    @opened="onDropBoxOpened"
                    @closed="onDropBoxClosed"
                    @value-changed="(e: any) => { if (e.value == null) cellInfo.setValue(null); }"
                    @key-down="(e: any) => onProdusBoxKeyDown(e, cellInfo)"
                >
                    <template #content>
                        <DxDataGrid
                            ref="innerProdusGridRef"
                            :data-source="dsProduse"
                            :selection="{ mode: 'single' }"
                            :focused-row-enabled="true"
                            :hover-state-enabled="true"
                            :selected-row-keys="cellInfo.value ? [cellInfo.value] : []"
                            :height="410"
                            :show-borders="false"
                            :show-column-lines="false"
                            :show-row-lines="true"
                            :filter-row="{ visible: true }"
                            :paging="{ pageSize: 25 }"
                            key-expr="Id"
                            @content-ready="onProdusGridContentReady"
                            @selection-changed="(e: any) => onProdusGridSelection(e, cellInfo)"
                            @key-down="(e: any) => onProdusGridKeyDown(e, cellInfo)"
                        >
                            <DxColumn data-field="Cod" caption="Cod" :width="110" />
                            <DxColumn data-field="CodIntern" caption="Cod Intern" :width="130" />
                            <DxColumn data-field="Nume" caption="Denumire" :calculate-filter-expression="produsNumeFilterExpression" />
                        </DxDataGrid>
                    </template>
                </DxDropDownBox>
            </template>


            <template #valutaListaEditor="{ data: cellInfo }">
                <DxSelectBox
                    :value="cellInfo.value"
                    :data-source="dsValuteLookup"
                    display-expr="Simbol"
                    value-expr="Id"
                    :search-enabled="true"
                    search-expr="Simbol"
                    :show-clear-button="true"
                    placeholder="Valută..."
                    @value-changed="(e: any) => { cellInfo.setValue(e.value); recalculeazaLinieLive({ dataField: 'PretListaValutaID', rowData: cellInfo.data, rowKey: cellInfo.row?.key, rowIndex: cellInfo.rowIndex, newValue: e.value }); }"
                    @key-down="(e: any) => { if (e.event.key === 'Enter') e.event.stopPropagation(); }"
                />
            </template>

            <template #furnizorEditor="{ data: cellInfo }">
                <DxSelectBox
                    :value="cellInfo.value"
                    :data-source="dsFurnizoriPO"
                    display-expr="Nume"
                    value-expr="ID"
                    :search-enabled="true"
                    search-expr="Nume"
                    :show-clear-button="true"
                    placeholder="Furnizor..."
                    @value-changed="(e: any) => { cellInfo.setValue(e.value); cellInfo.data.Furnizor = e.value ? { Nume: e.component.option('selectedItem')?.Nume } : null; }"
                    @key-down="(e: any) => { if (e.event.key === 'Enter') e.event.stopPropagation(); }"
                />
            </template>

            <!-- Coloane ascunse -->
            <DxColumn data-field="FurnizorID" caption="Furnizor" hint="Furnizorul de la care se achiziționează produsul de pe această linie." width="180" :visible="false" edit-cell-template="furnizorEditor">
                <DxLookup :data-source="dsFurnizoriLookup" display-expr="Nume" value-expr="ID" />
            </DxColumn>
            <DxColumn data-field="FurnizorPret" caption="Preț Furnizor" hint="Prețul de referință al produsului în relația cu furnizorul (poate fi prețul de achiziție sau prețul recomandat de producător)." data-type="number" :format="formatFixed2"
                width="115" :visible="false" />
            <DxColumn data-field="FurnizorPretValuta.Simbol" caption="Valută Furnizor" width="90" :visible="false"
                :allow-editing="false" />
            <DxColumn data-field="TermenLivrare" caption="Termen Livrare" data-type="date" format="dd.MM.yyyy"
                width="110" :visible="false" />
            <DxColumn data-field="GarantieLuni" caption="Garanție (luni)" width="110" :visible="false" />
            <DxColumn data-field="ValoareFaraTVA" caption="Val. fără TVA" hint="Baza de impozitare pentru linia respectivă; valoarea netă totală a produselor după ce s-au aplicat discounturile sau modificările de preț, dar înainte de adăugarea taxelor." data-type="number" :format="formatFixed2"
                width="130" :visible="false" :allow-editing="false" cell-template="valFaraTVACellTemplate" />
            <DxColumn data-field="Document.Tip.Cod" caption="Tip Document" width="110" :visible="false" :allow-editing="false" />
            <DxColumn data-field="Document.Numar" caption="Nr. Document" width="120" :visible="false" :allow-editing="false" />
            <DxColumn data-field="ClasificatieBugetara.Cod" caption="Clasificație Bugetară" width="150" :visible="false"
                :allow-editing="false" />
            <DxColumn data-field="ClasificatieBugetara.Denumire" caption="Den. Clasificație" width="180" :visible="false"
                :allow-editing="false" />
            <DxColumn data-field="SursaFinantare" caption="Sursă Finanțare" width="140" :visible="false" />
            <DxColumn data-field="Facturabil" caption="Facturabil" width="100" :visible="false" />
            <DxColumn data-field="ProdusCodCPV" caption="Cod CPV" width="100" :visible="false" />
            <DxColumn data-field="Produs.Nume" caption="Produs (nav)" width="180" :visible="false" :allow-editing="false" />
            <DxColumn data-field="Explicatie" caption="Explicație" width="160" :visible="false" />
            <DxColumn data-field="Anulata" caption="Anulată" width="90" :visible="false" :allow-editing="false" />
        </DxDataGrid>

        <!-- ── Popup Rezervare Linie ── -->
        <DxPopup :visible="showRezervareLiniePopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :width="460" :height="'auto'"
            :wrapper-attr="{ class: 'rezervare-stoc-popup' }" @hidden="showRezervareLiniePopup = false"
            @shown="onRezervareLiniePopupShown">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Confirmă', type: 'default', onClick: onConfirmRezerveLinie }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Renunță', stylingMode: 'outlined', onClick: () => showRezervareLiniePopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-box ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Rezervare stoc</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showRezervareLiniePopup = false">×</span>
                    </div>
                    <div class="rezervare-popup-body">
                        <p class="rezervare-produs-nume">{{ rezervareLinieProdusNume }}</p>
                        <div class="rezervare-field">
                            <span class="edit-label">Stoc disponibil</span>
                            <span class="edit-value">{{ rezervareLinieStocDisponibil ?? '—' }}</span>
                        </div>
                        <div class="rezervare-field">
                            <span class="edit-label">Cantitate de rezervat (max comandat: {{ rezervareLinieCantitateMax
                                }})</span>
                            <DxNumberBox ref="rezervareCantitateRef" v-model:value="rezervareLinieCantitate" :min="0" :max="rezervareLinieCantitateMax"
                                :format="formatRO" @enter-key="onConfirmRezerveLinie" />
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Generare Document ── -->
        <DxPopup :visible="showGenereazaDocPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :width="480" :height="360" @hidden="onCloseGenereazaDocPopup">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isGenereazandDoc ? 'Se generează...' : 'Generează', type: 'default', disabled: isGenereazandDoc || !genDocTipId, onClick: onGenereazaDocument }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Renunță', stylingMode: 'outlined', disabled: isGenereazandDoc, onClick: () => onCloseGenereazaDocPopup() }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-doc ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Generează document din comandă</span>
                                <span class="ehp-subtitle">{{ localOrderData?.Cod }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="onCloseGenereazaDocPopup()">×</span>
                    </div>
                    <div class="genereaza-doc-body">
                        <div class="edit-field">
                            <span class="edit-label">Tip document *</span>
                            <DxSelectBox
                                v-model:value="genDocTipId"
                                :data-source="dsTipuriDoc"
                                display-expr="Nume"
                                value-expr="Id"
                                placeholder="Selectează tipul documentului..."
                                :search-enabled="true"
                                search-expr="Nume"
                            />
                        </div>
                        <div class="edit-field">
                            <span class="edit-label">Data documentului</span>
                            <DxDateBox v-model:value="genDocData" display-format="dd.MM.yyyy" type="date" />
                        </div>
                        <div class="edit-field edit-field--wide">
                            <span class="edit-label">Observații</span>
                            <DxTextBox v-model:value="genDocObs" placeholder="Observații (opțional)..." />
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Editare Header ── -->
        <DxPopup :visible="showEditHeaderPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="640" :height="'auto'"
            @hidden="showEditHeaderPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isSavingHeader ? 'Se salvează...' : 'Salvează', type: 'default', disabled: isSavingHeader || !headerDraft.AgentId || !headerDraft.ExpCurierNume, onClick: onSaveHeader }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Anulează', stylingMode: 'outlined', disabled: isSavingHeader, onClick: () => showEditHeaderPopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-edit ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Editează comandă</span>
                                <span class="ehp-subtitle">{{ localOrderData?.Cod }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showEditHeaderPopup = false">×</span>
                    </div>
                    <div class="ehp-body">
                    <div class="form-group-card">
                        <div class="form-group-caption">Tip și Partener</div>
                        <div class="form-group-content">
                            <div class="fg g2">
                                <div class="field">
                                    <label>Tip Comandă</label>
                                    <DxSelectBox v-model:value="headerDraft.Tipcomanda"
                                        :data-source="categoriiComenzi"
                                        display-expr="Denumire"
                                        value-expr="TipComanda"
                                        :search-enabled="true"
                                        search-expr="Denumire"
                                        :show-clear-button="true"
                                        @value-changed="onTipComandaHeaderChanged" />
                                </div>
                                <div class="field">
                                    <label>{{ partenerLabelHeader }}</label>
                                    <DxSelectBox v-model:value="headerDraft.ClientId"
                                        :data-source="dsParteneriHeader"
                                        display-expr="Nume"
                                        value-expr="ID"
                                        :search-enabled="true"
                                        search-expr="Nume"
                                        :show-clear-button="true"
                                        :disabled="headerDraft.Tipcomanda == null"
                                        placeholder="Selectați partenerul..."
                                        @selection-changed="onPartenerHeaderChanged" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-group-card">
                        <div class="form-group-caption">Detalii comandă</div>
                        <div class="form-group-content">
                            <div class="fg g3">
                                <div class="field">
                                    <label>Agent <span class="required-star">*</span></label>
                                    <DxSelectBox v-model:value="headerDraft.AgentId"
                                        :data-source="dsAgenti"
                                        display-expr="Nume"
                                        value-expr="Id"
                                        :search-enabled="true"
                                        search-expr="Nume"
                                        :show-clear-button="true"
                                        placeholder="Selectați agentul..." />
                                </div>
                                <div class="field">
                                    <label>Valută</label>
                                    <DxSelectBox v-model:value="headerDraft.ValutaId"
                                        :data-source="dsValute"
                                        display-expr="Simbol"
                                        value-expr="Id"
                                        :search-enabled="true"
                                        search-expr="Simbol"
                                        :show-clear-button="true"
                                        placeholder="Selectați valuta..." />
                                </div>
                                <div class="field">
                                    <label>Data Comenzii</label>
                                    <DxDateBox v-model:value="headerDraft.DataComenzii" display-format="dd.MM.yyyy" type="date" />
                                </div>
                                <div class="field">
                                    <label>Termen Livrare</label>
                                    <DxDateBox v-model:value="headerDraft.TermenLivrare" display-format="dd.MM.yyyy" type="date"
                                        :show-clear-button="true" />
                                </div>
                                <div class="field span2">
                                    <label>PO (BT-13)</label>
                                    <DxSelectBox v-model:value="headerDraft.ProiectId"
                                        :data-source="dsProiecte"
                                        :display-expr="(item: any) => item ? `${item.Cod}${item.Nume ? ' — ' + item.Nume : ''}` : ''"
                                        value-expr="Id"
                                        :search-enabled="true"
                                        search-expr="Cod"
                                        :show-clear-button="true"
                                        placeholder="Selectați PO..." />
                                </div>
                                <div class="field span3">
                                    <label>Denumire</label>
                                    <DxTextBox v-model:value="headerDraft.Nume" :show-clear-button="true"
                                        placeholder="Denumirea comenzii..." />
                                </div>
                                <div class="field span3">
                                    <label>Observații</label>
                                    <DxTextBox v-model:value="headerDraft.Observatii" :show-clear-button="true"
                                        placeholder="Observații comenzii..." />
                                </div>
                                <div v-if="partenerLabelHeader === 'Client'" class="field span3 field-switch">
                                    <DxSwitch v-model:value="headerDraft.LivrareDirectaFurnizor" />
                                    <label class="switch-label">
                                        Livrare directă de la furnizor
                                        <span class="switch-hint">Furnizorul livrează marfa direct la client, fără să treacă prin gestiunea proprie — comanda poate fi pusă în lucru și facturată fără rezervare de stoc.</span>
                                    </label>
                                </div>
                                <div class="field span3 field-checkbox-group">
                                    <label>Livrat de <span class="required-star">*</span></label>
                                    <div class="checkbox-row">
                                        <div v-for="opt in EXP_CURIER_OPTIONS" :key="opt" class="checkbox-item"
                                            :class="{ 'checkbox-item--active': headerDraft.ExpCurierNume === (EXP_CURIER_LABELS[opt] || opt) }">
                                            <DxCheckBox :value="headerDraft.ExpCurierNume === (EXP_CURIER_LABELS[opt] || opt)"
                                                @value-changed="(e: any) => onToggleExpCurierTipHeader(opt, e.value)" />
                                            <span>{{ EXP_CURIER_LABELS[opt] || opt }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Adăugare Rapidă Linii ── -->
        <DxPopup :visible="showAdaugareRapidaPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="1400" :height="680"
            :wrapper-attr="{ class: 'rapida-adaugare-popup' }"
            @hidden="showAdaugareRapidaPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: rapida_isAdaugand ? 'Se adaugă...' : `Adaugă ${rapida_liniiGasite.length} linie(i)`, type: 'default', disabled: rapida_isAdaugand || rapida_liniiGasite.length === 0, onClick: onAdaugaToate }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Închide', stylingMode: 'outlined', onClick: () => showAdaugareRapidaPopup = false }" />
            <template #content>
                <div class="rapida-popup">
                    <!-- Header gradient -->
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-add ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Adăugare rapidă linii</span>
                                <span class="ehp-subtitle">Lipiți coduri — câte unul pe linie</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showAdaugareRapidaPopup = false">×</span>
                    </div>

                    <!-- Textarea coduri -->
                    <div class="rapida-coduri-section">
                        <textarea ref="rapidaTextareaRef" class="rapida-textarea"
                            v-model="rapida_coduri"
                            placeholder="Lipiți coduri cu Ctrl+V — câte unul pe linie..."
                            rows="4" />
                        <div v-if="rapida_isProcessing" class="rapida-status">Se procesează...</div>
                    </div>

                    <!-- Rezultate -->
                    <div class="rapida-results-section">
                        <template v-if="rapida_linii.length > 0">
                            <div class="rapida-results-caption">
                                Rezultate
                                <span v-if="rapida_liniiGasite.length > 0" class="rapida-badge-ok">{{ rapida_liniiGasite.length }} găsite</span>
                                <span v-if="rapida_liniiNegasite.length > 0" class="rapida-badge-err">{{ rapida_liniiNegasite.length }} negăsite</span>
                            </div>
                            <div class="rapida-table-scroll">
                                <table class="rapida-table">
                                    <thead>
                                        <tr>
                                            <th style="width:28px"></th>
                                            <th style="width:110px">Cod</th>
                                            <th style="min-width:140px">Denumire</th>
                                            <th style="width:90px">Cantitate</th>
                                            <th style="width:110px">Preț Unitar</th>
                                            <th style="min-width:220px">Furnizor</th>
                                            <th style="width:110px">Preț Furnizor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(linie, i) in rapida_linii" :key="i"
                                            :class="linie.gasit ? 'rapida-row-ok' : 'rapida-row-err'">
                                            <td class="rapida-status-cell">
                                                <i class="dx-icon" :class="linie.gasit ? 'dx-icon-check' : 'dx-icon-close'" />
                                            </td>
                                            <td class="rapida-cod-cell">{{ linie.cod }}</td>
                                            <td class="rapida-den-cell">
                                                <span v-if="linie.gasit">{{ linie.produs.Nume }}</span>
                                                <span v-else class="rapida-negasit">Nu a fost găsit</span>
                                            </td>
                                            <td class="rapida-input-cell">
                                                <DxNumberBox v-if="linie.gasit" v-model:value="linie.cantitate"
                                                    :min="0.0001" :format="formatRO" width="100%" />
                                            </td>
                                            <td class="rapida-input-cell">
                                                <DxNumberBox v-if="linie.gasit" v-model:value="linie.pretUnitar"
                                                    :min="0" :format="formatRO" width="100%" />
                                            </td>
                                            <td class="rapida-input-cell">
                                                <DxSelectBox v-if="linie.gasit"
                                                    v-model:value="linie.furnizorId"
                                                    :data-source="dsFurnizoriPO"
                                                    display-expr="Nume" value-expr="ID"
                                                    :search-enabled="true" search-expr="Nume"
                                                    :show-clear-button="true" width="100%" />
                                            </td>
                                            <td class="rapida-input-cell">
                                                <DxNumberBox v-if="linie.gasit" v-model:value="linie.pretFurnizor"
                                                    :min="0" :format="formatRO" width="100%" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                        <div v-else class="rapida-empty-state">
                            <i class="dx-icon dx-icon-paste" style="font-size:32px; color:#cbd5e1; margin-bottom:10px" />
                            <span>Lipiți coduri în câmpul de mai sus (Ctrl+V)</span>
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Eșalonări Livrare ── -->
        <DxPopup :visible="showEsalonariPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="900" :height="550"
            @hidden="showEsalonariPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Închide', stylingMode: 'outlined', onClick: () => showEsalonariPopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-clock ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Eșalonări livrare</span>
                                <span class="ehp-subtitle">{{ esalonariDetaliuNume }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showEsalonariPopup = false">×</span>
                    </div>
                    <div style="padding: 12px; flex: 1; min-height: 0;">
                        <DxDataGrid
                            ref="esalonariGridRef"
                            :data-source="esalonariStore"
                            key-expr="Id"
                            :show-borders="false"
                            :show-column-lines="false"
                            :show-row-lines="true"
                            :row-alternation-enabled="true"
                            :hover-state-enabled="true"
                            :height="370"
                            @row-inserting="onEsalonareInserting"
                            @row-updating="onEsalonareUpdating"
                            @row-removing="onEsalonareRemoving"
                        >
                            <DxEditing
                                :allow-updating="!isReadOnly"
                                :allow-adding="!isReadOnly"
                                :allow-deleting="!isReadOnly"
                                mode="row"
                                :use-icons="true"
                                :confirm-delete="false"
                            />
                            <DxColumn type="buttons" :width="80" />
                            <DxColumn data-field="DataLivrare" caption="Data Livrare" data-type="date"
                                format="dd.MM.yyyy" :width="140" alignment="center" />
                            <DxColumn data-field="Cantitate" caption="Cantitate" data-type="number"
                                :format="formatRO" :width="110" alignment="right" />
                            <DxColumn data-field="CantitateStoc" caption="Cant. Stoc" data-type="number"
                                :format="formatRO" :width="110" alignment="right" />
                            <DxColumn data-field="CantitateTotal" caption="Cant. Total" data-type="number"
                                :format="formatRO" :width="115" alignment="right" />
                            <DxColumn data-field="Status" caption="Status" :width="170"
                                cell-template="esalonareStatusTemplate">
                                <DxLookup :data-source="esalonareStatusuri" display-expr="Denumire" value-expr="Id" />
                            </DxColumn>
                            <DxColumn data-field="CantitateDorita" caption="Cant. Dorită" data-type="number"
                                :format="formatRO" :width="120" alignment="right" :visible="false" />
                            <DxColumn data-field="CantitateDinNecesar" caption="Din Necesar" data-type="number"
                                :format="formatRO" :width="120" alignment="right" :visible="false" />
                            <template #esalonareStatusTemplate="{ data }">
                                <span :style="getEsalonareStatusStyle(data.value)">
                                    {{ getEsalonareStatusNume(data.value) }}
                                </span>
                            </template>
                        </DxDataGrid>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Recepție (NIR) ── -->
        <DxPopup :visible="showReceptiePopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="820" :height="560"
            :wrapper-attr="{ class: 'receptie-popup' }" @hidden="showReceptiePopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isGenereazaReceptie ? 'Se salvează...' : 'Recepționează', type: 'default', disabled: isGenereazaReceptie, onClick: onConfirmReceptie }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Anulează', stylingMode: 'outlined', disabled: isGenereazaReceptie, onClick: () => showReceptiePopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-import ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Recepție Marfă — NIR</span>
                                <span class="ehp-subtitle">{{ localOrderData?.Cod }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showReceptiePopup = false">×</span>
                    </div>
                    <div style="padding:12px 16px;display:flex;gap:12px;flex-shrink:0">
                        <div class="aviz-field" style="flex:0 0 160px">
                            <label class="aviz-label">Data NIR</label>
                            <DxDateBox v-model:value="receptieFormData.Data" type="date" display-format="dd.MM.yyyy" :width="'100%'" />
                        </div>
                        <div class="aviz-field" style="flex:1">
                            <label class="aviz-label">Gestiune intrare (opțional)</label>
                            <DxSelectBox v-model:value="receptieFormData.GestiuneId"
                                :data-source="dsGestiuni" display-expr="Nume" value-expr="Id"
                                :show-clear-button="true" placeholder="Se utilizează gestiunea implicită" :width="'100%'" />
                        </div>
                        <div class="aviz-field" style="flex:1">
                            <label class="aviz-label">Observații</label>
                            <DxTextBox v-model:value="receptieFormData.Observatii" :width="'100%'" />
                        </div>
                    </div>
                    <div style="flex:1;min-height:0;padding:0 16px 8px">
                        <DxDataGrid :data-source="receptieLinii" key-expr="LinieId"
                            :show-borders="false" :show-column-lines="false" :show-row-lines="true"
                            :row-alternation-enabled="true" :hover-state-enabled="true" height="100%">
                            <DxEditing mode="cell" :allow-updating="true" />
                            <DxColumn data-field="ProdusNume" caption="Produs" :allow-editing="false" :min-width="160" />
                            <DxColumn data-field="CantitateComandataOrig" caption="Cant. Comandată" data-type="number"
                                :format="formatRO" :width="150" alignment="right" :allow-editing="false" />
                            <DxColumn data-field="CantitateReceptionata" caption="Cant. Recepționată" data-type="number"
                                :format="formatRO" :width="160" alignment="right" />
                            <DxColumn data-field="PretAchizitie" caption="Preț Achiziție" data-type="number"
                                :format="formatRO" :width="140" alignment="right" />
                        </DxDataGrid>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Generare Factură ── -->
        <DxPopup :visible="showGenereazaFacturaPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="520" :height="340"
            :wrapper-attr="{ class: 'aviz-popup' }" @hidden="showGenereazaFacturaPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isGenereazaFactura ? 'Se generează...' : 'Generează', type: 'default', disabled: isGenereazaFactura, onClick: onConfirmGenereazaFactura }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Anulează', stylingMode: 'outlined', disabled: isGenereazaFactura, onClick: () => showGenereazaFacturaPopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-money ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Generare Factură</span>
                                <span class="ehp-subtitle">{{ localOrderData?.Cod }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showGenereazaFacturaPopup = false">×</span>
                    </div>
                    <div style="padding: 16px; display: flex; flex-direction: column; gap: 12px;">
                        <div style="display:flex;gap:12px">
                            <div class="aviz-field" style="flex:1">
                                <label class="aviz-label">Data facturii</label>
                                <DxDateBox v-model:value="facturaFormData.Data" type="date" display-format="dd.MM.yyyy" :width="'100%'" />
                            </div>
                            <div class="aviz-field" style="flex:1">
                                <label class="aviz-label">Scadență (opțional)</label>
                                <DxDateBox v-model:value="facturaFormData.Scadenta" type="date" display-format="dd.MM.yyyy" :show-clear-button="true" :width="'100%'" />
                            </div>
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Tip factură</label>
                            <DxSelectBox v-model:value="facturaFormData.TipFacturaId"
                                :data-source="tipuriFactura" display-expr="Nume" value-expr="Id"
                                :search-enabled="true" search-expr="Nume"
                                placeholder="Alege tipul facturii" :width="'100%'" />
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Document gestiune (opțional — lasă gol pentru a factura comanda direct)</label>
                            <DxSelectBox v-model:value="facturaFormData.AvizId"
                                :data-source="avize" display-expr="Numar" value-expr="Id"
                                :show-clear-button="true" placeholder="Facturare directă din comandă" :width="'100%'" />
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Generare Aviz Expediție ── -->
        <DxPopup :visible="showGenereazaAvizPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="480" :height="390"
            :wrapper-attr="{ class: 'aviz-popup' }" @hidden="showGenereazaAvizPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isGenereazaAviz ? 'Se generează...' : 'Generează', type: 'default', disabled: isGenereazaAviz, onClick: onConfirmGenereazaAviz }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Anulează', stylingMode: 'outlined', disabled: isGenereazaAviz, onClick: () => showGenereazaAvizPopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-export ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Generare Aviz Expediție</span>
                                <span class="ehp-subtitle">{{ localOrderData?.Cod }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showGenereazaAvizPopup = false">×</span>
                    </div>
                    <div style="padding: 16px; display: flex; flex-direction: column; gap: 12px;">
                        <div class="aviz-field">
                            <label class="aviz-label">Data avizului</label>
                            <DxDateBox v-model:value="avizFormData.Data" type="date" display-format="dd.MM.yyyy"
                                :width="'100%'" />
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Gestiune (opțional)</label>
                            <DxSelectBox v-model:value="avizFormData.GestiuneId"
                                :data-source="dsGestiuni"
                                display-expr="Nume" value-expr="Id"
                                :show-clear-button="true"
                                placeholder="Se utilizează gestiunea implicită"
                                :width="'100%'" />
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Delegat</label>
                            <DxTextBox v-model:value="avizFormData.ExpediereNume" placeholder="Nume delegat" :width="'100%'" />
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Mijloc transport</label>
                            <DxTextBox v-model:value="avizFormData.ExpediereMijloc" placeholder="Nr. mașină / firmă transport" :width="'100%'" />
                        </div>
                        <div class="aviz-field">
                            <label class="aviz-label">Observații</label>
                            <DxTextBox v-model:value="avizFormData.Observatii" placeholder="Observații aviz" :width="'100%'" />
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>

        <!-- ── Popup Denumiri Personalizate Produse ── -->
        <DxPopup :visible="showDenumiriPopup" :show-title="false" :drag-enabled="true"
            :show-close-button="false" :close-on-outside-click="false" :width="920" :height="560"
            :wrapper-attr="{ class: 'denumiri-popup' }" @hidden="showDenumiriPopup = false">
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: isSavingDenumiri ? 'Se salvează...' : 'Salvează', type: 'default', disabled: isSavingDenumiri, onClick: onSaveDenumiri }" />
            <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
                :options="{ text: 'Închide', stylingMode: 'outlined', disabled: isSavingDenumiri, onClick: () => showDenumiriPopup = false }" />
            <template #content>
                <div class="ehp-wrapper">
                    <div class="ehp-header">
                        <div class="ehp-header-main">
                            <i class="dx-icon dx-icon-user ehp-icon" />
                            <div class="ehp-texts">
                                <span class="ehp-title">Denumire partener — {{ denumiriProdusNume }}</span>
                                <span class="ehp-subtitle">Partener: {{ localOrderData?.Client?.Nume ?? '—' }}</span>
                            </div>
                        </div>
                        <span class="ehp-close" @click="showDenumiriPopup = false">×</span>
                    </div>
                    <div class="denumiri-body">
                        <DxDataGrid
                            ref="denumiriGridRef"
                            :data-source="denumiriRows"
                            key-expr="ProdusID"
                            :show-borders="false"
                            :show-column-lines="false"
                            :show-row-lines="true"
                            :row-alternation-enabled="true"
                            :hover-state-enabled="true"
                            :column-auto-width="false"
                            height="390"
                        >
                            <DxEditing :allow-updating="true" mode="cell" />
                            <DxColumn data-field="PartenerNume" caption="Partener" :allow-editing="false" :width="160" />
                            <DxColumn data-field="ProdusNume" caption="Denumire internă" :allow-editing="false" />
                            <DxColumn data-field="ProdusCod" caption="Cod intern" :allow-editing="false" :width="110" />
                            <DxColumn data-field="Cod" caption="Cod pt. partener" :width="140" />
                            <DxColumn data-field="Nume" caption="Denumire pt. partener" />
                        </DxDataGrid>
                    </div>
                </div>
            </template>
        </DxPopup>

        <DxPopup :visible="isPdfPreviewVisible" :width="1000" :height="800" :drag-enabled="true"
            :resize-enabled="true" :hide-on-outside-click="true" @update:visible="v => { if (!v) closePdfPreview(); }">
            <template #title>
                <div style="background-color: #ff2c21; font-size: 15px; color: white; font-weight: bold; padding: 10px; padding-left: 20px;">
                    {{ pdfFileName || 'Previzualizare factură' }}
                </div>
            </template>
            <div style="display:flex; flex-direction:column; height:100%;">
                <div style="flex:1; border-top:1px solid #ddd;">
                    <iframe v-if="pdfBlobUrl" :src="pdfPreviewSrc" style="width:100%; height:100%; border:0;" />
                </div>
                <div style="display:flex; justify-content:center; align-items:center; gap:16px; padding:12px;">
                    <DxButton text="Descarcă PDF" icon="download" type="success" @click="downloadPreviewReport" />
                    <DxButton text="Închide" type="danger" icon="close" @click="closePdfPreview" />
                </div>
            </div>
        </DxPopup>

    </div>

    <teleport to="body">
        <div v-if="metaTooltip.visible" class="meta-tooltip-popup" :style="metaTooltip.style">
            <span class="meta-tooltip-label">{{ metaTooltip.label }}</span>
            <p class="meta-tooltip-text">{{ metaTooltip.text }}</p>
        </div>
    </teleport>
</template>

<script lang="ts" setup>
import { ref, shallowRef, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import * as signalR from '@microsoft/signalr';
import DxPopup, { DxToolbarItem } from 'devextreme-vue/popup';
import DxButton from 'devextreme-vue/button';
import DxTextBox from 'devextreme-vue/text-box';
import DxDateBox from 'devextreme-vue/date-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxSelectBox from 'devextreme-vue/select-box';
import DxSwitch from 'devextreme-vue/switch';
import DxCheckBox from 'devextreme-vue/check-box';
import DxDataGrid, { DxColumn, DxEditing, DxStateStoring, DxToolbar, DxItem, DxLookup, DxSelection, DxRequiredRule } from 'devextreme-vue/data-grid';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import { jsPDF } from 'jspdf';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import AppSetting from '@/settings/appSettings';
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';
import DxLoadPanel from 'devextreme-vue/load-panel';
import ProComenziService from '@/services/Comercial/ProComenzi';
import DOC_DocumenteService from '@/services/Documente/DOC_Documente';
import JwtServices from '@/services/JwtServices';
import http from '@/settings/apiSettings';
import { usePrintReport } from '@/composable/usePrintReport';

interface Props {
    comandaId: string;
    orderData?: Record<string, any>;
    isNew?: boolean;
    // Setat de PRO_ComenziView cât timp verifică el însuși dacă poate schimba tab-ul
    // (confirmSwitchAway) — evită ca auto-salvarea de mai jos să intervină concurent și
    // să salveze silențios exact linia pe care părintele vrea s-o trateze explicit
    // (Salvează/Renunță/Rămân).
    suppressAutoSave?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
    (e: 'open-order', order: Record<string, any>): void;
    (e: 'unsaved-changes-change', hasChanges: boolean): void;
    (e: 'grid-init-failed'): void;
}>();

// ── Local state ──
const localOrderData = ref<Record<string, any> | null>(null);
const isLoading = ref(false);
const {
    isPrinting, printReport,
    pdfBlobUrl, pdfPreviewSrc, pdfFileName, isPdfPreviewVisible,
    previewReport, downloadPreviewReport, closePdfPreview,
} = usePrintReport();
const categoriiComenzi = ref<any[]>([]);

const tipComandaNume = computed(() => {
    const tip = localOrderData.value?.Tipcomanda;
    if (tip == null) return null;
    return categoriiComenzi.value.find(c => c.TipComanda === tip)?.Denumire ?? null;
});

// ── SignalR — order presence lock ──
const hubConnection = ref<signalR.HubConnection | null>(null);
const editingByUser = ref<string | null>(null);

async function connectSignalR(orderId: string) {
    try {
        const conn = new signalR.HubConnectionBuilder()
            .withUrl(AppSetting.API_Url_ComercialSignalR)
            .withAutomaticReconnect()
            .build();

        conn.on('OrderLocked', (id: string, username: string) => {
            if (id === orderId) editingByUser.value = username;
        });
        conn.on('OrderUnlocked', (id: string) => {
            if (id === orderId) editingByUser.value = null;
        });

        await conn.start();

        let username = 'Utilizator';
        try { username = JwtServices.GetUsername(); } catch { /* ignore */ }

        const lockedBy = await conn.invoke<string | null>('LockOrder', orderId, username);
        if (lockedBy) editingByUser.value = lockedBy;

        hubConnection.value = conn;
    } catch (err) {
        console.warn('SignalR unavailable:', err);
    }
}

async function disconnectSignalR(orderId: string) {
    const conn = hubConnection.value;
    if (!conn) return;
    try { await conn.invoke('UnlockOrder', orderId); } catch { /* ignore */ }
    try { await conn.stop(); } catch { /* ignore */ }
    hubConnection.value = null;
    editingByUser.value = null;
}

let connectedOrderId: string | null = null;

async function syncSignalRConnection(orderId: string) {
    if (connectedOrderId === orderId) return;
    if (connectedOrderId) await disconnectSignalR(connectedOrderId);
    connectedOrderId = orderId;
    await connectSignalR(orderId);
}
const detaliiStore = shallowRef<any>(null);
const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
const dsProduse = {
    store: {
        type: 'odata',
        url: AppSetting.API_Url_ComercialOdata + 'PRO_Produse',
        key: 'Id',
        keyType: 'Guid',
        version: 4,
        beforeSend: (e: any) => {
            e.headers = { Authorization: 'Bearer ' + (localStorage.getItem('ERA_Jwt') ?? '') };
            e.params.$expand = 'PretValuta($select=Curs)';
        },
    },
    select: ['Id', 'Cod', 'CodIntern', 'Nume', 'PretVanzare', 'Pret', 'TvaCurent', 'GarantieLuni', 'PretValutaId'],
    paginate: true, pageSize: 30,
};

// ── Generare document ──
const showGenereazaDocPopup = ref(false);
const isGenereazandDoc = ref(false);
const genDocTipId = ref<string | null>(null);
const genDocData = ref<Date>(new Date());
const genDocObs = ref('');

const dsTipuriDoc = new DataSource({
    store: new ODataStore({
        url: AppSetting.API_Url_DocumenteOdata + 'DOC_Tipuri',
        key: 'Id',
        version: 4,
        beforeSend: (e: any) => {
            e.headers = { Authorization: 'Bearer ' + (localStorage.getItem('ERA_Jwt') ?? '') };
        },
    }),
    select: ['Id', 'Nume', 'Cod'],
    filter: ['AreComanda', '=', true],
    sort: [{ selector: 'Nume', desc: false }],
});

// Tipuri de factură selectabile în popup-ul „Generează Factură” — restricționate de backend
// (PRO_ComenziRepository.GetTipuriFacturaPermise) după dreptul explicit "DOC_Tipuri" din
// AUTH_DrepturiUtilizatori; dacă userul nu are niciun drept explicit configurat, backendul nu
// aplică nicio restricție (vezi comentariul din repository).
// Încărcat o singură dată într-un array simplu (nu ca DataSource live) — altfel DxSelectBox nu
// afișează valoarea implicită preselectată dacă popup-ul se deschide înainte ca request-ul remote
// să se termine (rămâne gol până deschizi/închizi dropdown-ul manual).
const tipuriFactura = ref<any[]>([]);
async function loadTipuriFactura() {
    try {
        const resp = await ProComenziService.getTipuriFacturaPermise();
        tipuriFactura.value = resp.data ?? [];
    } catch { tipuriFactura.value = []; }
}

function onOpenGenereazaDocPopup() {
    genDocTipId.value = null;
    genDocData.value = new Date();
    genDocObs.value = localOrderData.value?.Observatii ?? '';
    showGenereazaDocPopup.value = true;
}

function onCloseGenereazaDocPopup() {
    showGenereazaDocPopup.value = false;
    genDocTipId.value = null;
    genDocData.value = new Date();
    genDocObs.value = '';
}

async function onGenereazaDocument() {
    if (!genDocTipId.value) return;
    isGenereazandDoc.value = true;
    try {
        const dto = {
            ComandaId: props.comandaId,
            TipDocumentId: genDocTipId.value,
            Data: genDocData.value,
            Observatii: genDocObs.value || null,
        };
        const resp = await DOC_DocumenteService.genereazaDocumentDinComanda(dto);
        const numar = resp.data?.Document?.Numar ?? '';
        onCloseGenereazaDocPopup();
        toast(`Documentul ${numar ? `nr. ${numar} ` : ''}a fost generat cu succes.`, 'success', 3000);
        const avertismente: string[] = resp.data?.Avertismente ?? [];
        for (const msg of avertismente) {
            toast(msg, 'warning', 6000);
        }
        await refreshLocalData();
        // Documentul generat generic poate fi orice tip (aviz, factură, NIR etc.) — reîmprospătăm
        // toate listele de documente ca să apară imediat în tab-ul corect (Avize/Facturi/Recepții
        // sau Documente generice), nu doar la reîncărcarea completă a paginii.
        await Promise.all([
            refreshCantitatiRezervate(), refreshStocuriProduse(),
            refreshAvize(), refreshFacturi(), refreshReceptii(), refreshComenziStorno(),
        ]);
        gridRef.value?.instance?.refresh();
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isGenereazandDoc.value = false;
    }
}

// ── Drepturi granulare ──
const drepturi = ref({
    CreeazaComenzi: true, StergeComenzi: true, DuplicaComenzi: true,
    AnuleazaComenzi: true, AnuleazaFacturi: true, AnuleazaAvize: true, ModificaPretComenzi: true, RezervaComenzi: true,
    VedeToateComenzi: true, EditeazaComenzi: true, GenereazaAvize: true, ReceptioneazaMarfa: true, GenereazaFacturi: true,
});

// ── Rezervări ──
const isRezervandToate = ref(false);
const showRezervareLiniePopup = ref(false);
const rezervaLineId = ref('');
const rezervareLinieProdusNume = ref('');
const rezervareLinieProdusId = ref('');
const rezervareLinieCantitateMax = ref(0);
const rezervareLinieCantitate = ref(0);
const rezervareCantitateRef = ref<any>(null);
const cantitatiRezervate = shallowRef<Record<string, number>>({});
const stocuriProduse = shallowRef<Record<string, any>>({});
const calcCache = shallowRef<Record<string, any>>({});
const liniiGrid = ref<any[]>([]);
const liniiSelectate = ref<string[]>([]);
const isBatchEditMode = ref(false);
const isFullscreen = ref(false);

function toggleFullscreen() {
    isFullscreen.value = !isFullscreen.value;
}
const isGenerandStorno = ref(false);
const isGenerandComenziFurnizori = ref(false);
const isRefreshingCurs = ref(false);
const refreshCursNecesar = ref(false);
const cursEUR = ref<number | null>(null);
const cursUSD = ref<number | null>(null);

const metaTooltip = ref({ visible: false, style: {} as Record<string, string>, label: '', text: '' });

function showMetaTooltip(e: MouseEvent, label: string, text: string) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    metaTooltip.value = {
        visible: true,
        style: {
            top: `${rect.bottom + 8}px`,
            left: `${Math.min(rect.left, window.innerWidth - 420)}px`,
        },
        label,
        text,
    };
}

function hideMetaTooltip() {
    metaTooltip.value.visible = false;
}

// Cursul BNR "de azi" e mereu rândul cel mai recent STRICT ÎNAINTE de azi — BNR publică
// după-amiaza cursul valabil de-a doua zi, deci rândul datat cu ziua curentă e de fapt cursul
// de "mâine", neefectiv încă (aceeași regulă ca backend: GetCursRon/CalcolaLinie, portată din
// legacy ERP_CursValutarLaData.sql: "Data < @Data order by Data desc").
function inceputDeAzi(): string {
    const azi = new Date();
    azi.setHours(0, 0, 0, 0);
    return azi.toISOString();
}

async function loadCursuriValutare() {
    try {
        const inainteDeAzi = inceputDeAzi();
        const [respEUR, respUSD] = await Promise.all([
            http.clientComercialOdata.get(`ERP_CursValutar?$filter=Simbol eq 'EUR' and Data lt ${inainteDeAzi}&$orderby=Data desc&$top=1&$select=Curs`),
            http.clientComercialOdata.get(`ERP_CursValutar?$filter=Simbol eq 'USD' and Data lt ${inainteDeAzi}&$orderby=Data desc&$top=1&$select=Curs`),
        ]);
        cursEUR.value = respEUR.data?.value?.[0]?.Curs ?? null;
        cursUSD.value = respUSD.data?.value?.[0]?.Curs ?? null;
    } catch { /* ignore */ }
}

// ── Ultimul curs BNR per valută (pentru coloana "Curs Pret Lista") ──
const latestCursuriByValuta = ref<Record<string, number>>({});

async function loadLatestCursuriToateValutele() {
    try {
        const resp = await http.clientComercialOdata.get(
            `ERP_CursValutar?$filter=Data lt ${inceputDeAzi()}&$orderby=Data desc&$top=500&$select=ValutaID,Curs,Data`
        );
        const rows: any[] = resp.data?.value ?? [];
        const map: Record<string, number> = {};
        for (const r of rows) {
            if (r.ValutaID == null || r.Curs == null) continue;
            const key = String(r.ValutaID).toLowerCase();
            if (!(key in map)) map[key] = r.Curs;
        }
        latestCursuriByValuta.value = map;
    } catch { /* ignore */ }
}

// ── Eșalonări livrare ──
const showEsalonariPopup = ref(false);
const esalonariDetaliuId = ref<string | null>(null);
const esalonariDetaliuNume = ref('');
const esalonariStore = shallowRef<any>(null);
const esalonariGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);

const esalonareStatusuri = [
    { Id: 1, Denumire: 'Așteptare' },
    { Id: 2, Denumire: 'Rezervată' },
    { Id: 3, Denumire: 'Programat Incomplet' },
    { Id: 4, Denumire: 'Programată' },
    { Id: 5, Denumire: 'Lansat Incomplet' },
    { Id: 6, Denumire: 'Lansată' },
    { Id: 7, Denumire: 'Închisă' },
    { Id: 8, Denumire: 'Anulată' },
];

const ESALONARE_STATUS_COLORS: Record<number, { bg: string; color: string }> = {
    1: { bg: '#f1f5f9', color: '#475569' },
    2: { bg: '#eff6ff', color: '#1d4ed8' },
    3: { bg: '#fef3c7', color: '#92400e' },
    4: { bg: '#ecfdf5', color: '#065f46' },
    5: { bg: '#fef3c7', color: '#92400e' },
    6: { bg: '#dcfce7', color: '#166534' },
    7: { bg: '#f1f5f9', color: '#64748b' },
    8: { bg: '#fef2f2', color: '#991b1b' },
};

function getEsalonareStatusNume(status: number | null | undefined): string {
    if (status == null) return '—';
    return esalonareStatusuri.find(s => s.Id === status)?.Denumire ?? String(status);
}

function getEsalonareStatusStyle(status: number | null | undefined): Record<string, string> {
    const t = ESALONARE_STATUS_COLORS[status ?? 0] ?? { bg: '#f1f5f9', color: '#475569' };
    return {
        background: t.bg, color: t.color,
        padding: '2px 8px', borderRadius: '12px', fontWeight: '600',
        fontSize: '11px', whiteSpace: 'nowrap',
    };
}

// ── Denumiri personalizate produse ──────────────────────────────────────────
const showDenumiriPopup = ref(false);
const isSavingDenumiri = ref(false);
const denumiriGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
const denumiriProdusNume = ref('');

interface DenumireRow {
    ProdusID: string;
    ProdusNume: string;
    ProdusCod: string;
    PartenerNume: string;
    Cod: string;
    Nume: string;
    _existentId: string | null;
    _originalCod: string;
    _originalNume: string;
}
const denumiriRows = ref<DenumireRow[]>([]);

const toateRezervate = computed(() => {
    // Comenzile drop-ship (LivrareDirectaFurnizor) nu trec prin gestiunea proprie — nu are
    // sens să cerem rezervare de stoc intern, la fel ca la validarea din backend.
    if (localOrderData.value?.LivrareDirectaFurnizor) return true;
    const liniiActive = liniiGrid.value.filter(l => l.ProdusID && !l.Anulata);
    if (liniiActive.length === 0) return false;
    return liniiActive.every(l => (cantitatiRezervate.value[l.ID] ?? 0) >= (l.Cantitate ?? 0));
});

// "Generează comenzi furnizori" — are sens doar dacă există măcar o linie activă cu furnizor
// setat (vezi onGenereazaComenziFurnizori: furnizorul e cel de pe fiecare linie de produs).
const existaFurnizoriPeLinii = computed(() =>
    liniiGrid.value.some(l => l.ProdusID && !l.Anulata && l.FurnizorID)
);

// "Rezervă toate" — are sens doar dacă există măcar o linie activă pe comandă.
const existaLiniiActive = computed(() =>
    liniiGrid.value.some(l => l.ProdusID && !l.Anulata)
);

// "Generează document" cere comanda Confirmata sau InLucru și complet rezervată — mesajul
// explică exact ce condiție lipsește.
const STARI_PERMISE_GENEREAZA_DOC = ['Confirmata', 'InLucru'];
const genereazaDocDisabledHint = computed(() => {
    if (!STARI_PERMISE_GENEREAZA_DOC.includes(localOrderData.value?.Stare ?? ''))
        return 'Documentul poate fi generat doar cât timp comanda e Confirmată sau În Lucru.';
    if (!toateRezervate.value)
        return 'Comanda trebuie să fie complet rezervată pentru a genera documentul.';
    return undefined;
});

async function refreshCantitatiRezervate() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getCantitatiRezervate(props.comandaId);
        cantitatiRezervate.value = resp.data ?? {};
    } catch { cantitatiRezervate.value = {}; }
}

async function refreshStocuriProduse() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getStocuriProduse(props.comandaId);
        stocuriProduse.value = resp.data ?? {};
    } catch { stocuriProduse.value = {}; }
}

const isReadOnly = computed(() =>
    ['Finalizata', 'Anulata'].includes(localOrderData.value?.Stare ?? '') ||
    editingByUser.value !== null ||
    localOrderData.value?.Facturat === true
);

// Liniile comenzii nu mai pot fi editate/adăugate/șterse din grid odată intrată "În Lucru" —
// diferit de isReadOnly (care ar ascunde și "Rezervă toate"/"Refresh curs", butoane necesare
// tocmai în starea InLucru).
const liniiReadOnly = computed(() =>
    isReadOnly.value || localOrderData.value?.Stare === 'InLucru'
);

function canEditRow(options: any): boolean {
    if (liniiReadOnly.value) return false;
    return !options.row?.data?.DocumentID;
}

// Referință stabilă (nu se schimbă reactiv) — citește isBatchEditMode intern la execuție
function allowUpdatingFn(options: any): boolean {
    if (!isBatchEditMode.value) return false;
    return canEditRow(options);
}

function canDeleteRow(options: any): boolean {
    if (liniiReadOnly.value) return false;
    return !options.row?.data?.DocumentID;
}

// ── State machine ──
interface StateBtn { text: string; stareNoua: string; type: string; disabled?: boolean; disabledHint?: string; }

const currentCategorie = computed(() => {
    const tip = localOrderData.value?.Tipcomanda;
    if (tip == null) return null;
    return categoriiComenzi.value.find((c: any) => c.TipComanda === tip) ?? null;
});

const isClientOrder = computed(() => {
    return currentCategorie.value?.TipFurnizor !== true;
});

// Coduri TipComanda pentru comenzi storno — aceleași ca în backend
// (PRO_ComenziRepository.GenereazaStornoComanda: 3 = storno client, 9 = storno furnizor).
const isComandaStorno = computed(() => {
    const tip = localOrderData.value?.Tipcomanda;
    return tip === 3 || tip === 9;
});

const stateButtons = computed<StateBtn[]>(() => {
    switch (localOrderData.value?.Stare) {
        case 'InAsteptare': return [
            { text: 'Confirmă', stareNoua: 'Confirmata', type: 'default' },
        ];
        case 'Confirmata': return [
            { text: 'Marchează În Lucru', stareNoua: 'InLucru', type: 'default' },
            { text: 'Repune în Așteptare', stareNoua: 'InAsteptare', type: 'normal' },
        ];
        case 'InLucru': return [
            { text: 'Finalizează', stareNoua: 'Finalizata', type: 'success' },
            { text: 'Înapoi la Confirmată', stareNoua: 'Confirmata', type: 'normal' },
        ];
        default: return [];
    }
});

// Butonul "Anulează" e independent de switch-ul de mai sus — rămâne mereu vizibil cât timp
// comanda e într-o stare activă (nu deja Finalizata/Anulata), indiferent de restul tranzițiilor.
// E însă dezactivat (nu ascuns) dacă utilizatorul nu are dreptul AnuleazaComenzi — backend-ul
// verifică oricum dreptul la SchimbaStare (case "Anulata") și respinge cu eroare clară, dar
// dezactivarea din UI evită un click care oricum ar eșua.
const visibleStateButtons = computed(() => {
    const btns = [...stateButtons.value];
    const stare = localOrderData.value?.Stare;
    const poateAnula = !!stare && stare !== 'Anulata' && stare !== 'Finalizata';
    if (poateAnula) {
        btns.push({
            text: 'Anulează', stareNoua: 'Anulata', type: 'danger',
            disabled: !drepturi.value.AnuleazaComenzi,
            disabledHint: !drepturi.value.AnuleazaComenzi ? 'Nu aveți dreptul de a anula comenzi.' : undefined,
        });
    }
    return btns;
});

async function onStateButtonClick(btn: StateBtn) {
    // Storno (client/furnizor) nu rezervă stoc niciodată (blocat hard în backend,
    // PRO_RezervariRepository.IncarcaContextComanda) — la fel ca drop-ship, tranziția în
    // InLucru nu trebuie condiționată de rezervare. Pentru storno client, singura precondiție
    // reală pentru facturare e recepția (NIR) — verificată separat de backend în GenereazaFactura.
    if (btn.stareNoua === 'InLucru' && !localOrderData.value?.LivrareDirectaFurnizor && !isComandaStorno.value) {
        const linii: any[] = gridRef.value?.instance?.getDataSource().items() ?? [];
        // Serviciile (Produs.Stocabil=false) nu se rezervă niciodată — doar produsele stocabile
        // trebuie verificate înainte de a permite tranziția în InLucru.
        const liniiActive = linii.filter(l => l.ProdusID && !l.Anulata && l.Produs?.Stocabil !== false);
        const nerezervate = liniiActive.filter(l => (cantitatiRezervate.value[l.ID] ?? 0) < (l.Cantitate ?? 0));
        if (nerezervate.length > 0) {
            toast(`${nerezervate.length} produs(e) nu sunt rezervate complet. Comanda trebuie să fie complet rezervată pentru a fi pusă în lucru.`, 'error', 4000);
            return;
        }
    }
    if (btn.stareNoua === 'InLucru') {
        const linii: any[] = gridRef.value?.instance?.getDataSource().items() ?? [];
        const liniiPretZero = linii.filter(l => l.ProdusID && !l.Anulata && Number(l.PretNegociat ?? l.PretUnitar ?? 0) === 0);
        if (liniiPretZero.length > 0) {
            const ok = await custom({
                title: 'Preț 0',
                messageHtml: `<b>${liniiPretZero.length}</b> linie(i) au prețul <b>0</b>:<br>`
                    + liniiPretZero.slice(0, 5).map(l => `• ${l.ProdusNume ?? l.Produs?.Nume ?? 'produs necunoscut'}`).join('<br>')
                    + (liniiPretZero.length > 5 ? '<br>…' : '')
                    + '<br><br>Ești sigur că vrei să pui comanda În Lucru cu aceste linii la preț 0?',
                buttons: [
                    { text: 'Da, continuă', type: 'default', onClick: () => true },
                    { text: 'Renunț', stylingMode: 'outlined', onClick: () => false },
                ],
            }).show();
            if (!ok) return;
        }
    }
    if (btn.stareNoua === 'Anulata') {
        const ok = await custom({
            title: 'Confirmare anulare',
            messageHtml: 'Ești sigur că vrei să anulezi această comandă? Acțiunea este ireversibilă.',
            buttons: [
                { text: 'Da, anulează', type: 'danger', onClick: () => true },
                { text: 'Nu', stylingMode: 'outlined', onClick: () => false },
            ],
        }).show();
        if (!ok) return;
    }
    doSchimbaStare(btn.stareNoua);
}

async function doSchimbaStare(stareNoua: string) {
    try {
        await ProComenziService.schimbaStare(props.comandaId, stareNoua);
        await refreshLocalData();
        toast(`Starea a fost schimbată în '${stareNoua}'.`, 'success', 2000);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    }
}

// ── Header edit ──
const showEditHeaderPopup = ref(false);
const isSavingHeader = ref(false);
const headerDraft = ref<{
    ClientId: string | null; AgentId: string | null; ValutaId: string | null;
    Tipcomanda: number | null; DataComenzii: any; TermenLivrare: any; Nume: string; Observatii: string;
    ProiectId: string | null; LivrareDirectaFurnizor: boolean; ExpCurierNume: string;
}>({
    ClientId: null, AgentId: null, ValutaId: null, Tipcomanda: null,
    DataComenzii: null, TermenLivrare: null, Nume: '', Observatii: '', ProiectId: null,
    LivrareDirectaFurnizor: false, ExpCurierNume: '',
});

const EXP_CURIER_OPTIONS = ['CURIER', 'SOFER', 'FURNIZOR', 'SEDIU'];
const EXP_CURIER_LABELS: Record<string, string> = { SEDIU: 'RIDICARE DE LA SEDIU' };

function onToggleExpCurierTipHeader(opt: string, checked: boolean) {
    const label = EXP_CURIER_LABELS[opt] || opt;
    if (checked) {
        headerDraft.value.ExpCurierNume = label;
    } else if (headerDraft.value.ExpCurierNume === label) {
        headerDraft.value.ExpCurierNume = '';
    }
}

const dsParteneriHeader = shallowRef<DataSource | null>(null);
const dsAgenti = ref<DataSource | null>(null);
const dsValute = ref<DataSource | null>(null);
const dsProiecte = shallowRef<DataSource | null>(null);

const dsValuteLookup = new ODataStore({
    url: AppSetting.API_Url_ComercialOdata + 'ERP_Valute',
    key: 'Id',
    version: 4,
    beforeSend: (e: any) => {
        e.headers = { Authorization: 'Bearer ' + (localStorage.getItem('ERA_Jwt') ?? '') };
    },
});

// Nepaginat (spre deosebire de dsFurnizoriPO, folosit pentru dropdown-ul din "Adaugă rapid") —
// DxLookup din coloana grid-ului trebuie să poată rezolva/afișa orice furnizor deja salvat pe
// linie, nu doar primii 30 (prima pagină) din dsFurnizoriPO.
// IMPORTANT: obiect de configurare simplu (DataSourceOptions), NU o instanță `new DataSource(...)`
// — la fel ca `dsValuteLookup` de mai sus. Un `<DxLookup>` legat de coloana gridului (afișarea în
// modul necitire) rezolvă valoarea prin `lookup.dataSource`, care așteaptă un store/config brut;
// o instanță DataSource înfășurată acolo nu se rezolvă (rămâne gol în afișare), deși valoarea
// e salvată corect — de asta furnizorul apărea doar în editor (care citește direct cellInfo.value).
const dsFurnizoriLookup = {
    store: new ODataStore({
        url: AppSetting.API_Url_ComercialOdata + 'PAR_Parteneri',
        key: 'ID',
        keyType: 'Guid',
        version: 4,
        beforeSend: (e: any) => {
            e.headers = { Authorization: 'Bearer ' + (localStorage.getItem('ERA_Jwt') ?? '') };
        },
    }),
    select: ['ID', 'Nume'],
    paginate: false,
};

function initEditHeaderDataSources() {
    if (dsAgenti.value && dsValute.value) return;
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const hdr = (e: any) => { e.headers = { Authorization: 'Bearer ' + token }; };
    dsAgenti.value = new DataSource({
        store: new ODataStore({ url: AppSetting.API_Url_ComercialOdata + 'COM_Agenti', key: 'Id', keyType: 'Guid', version: 4, beforeSend: hdr }),
        select: ['Id', 'Nume'],
        sort: [{ selector: 'Nume', desc: false }],
    });
    dsValute.value = new DataSource({
        store: new ODataStore({ url: AppSetting.API_Url_ComercialOdata + 'ERP_Valute', key: 'Id', keyType: 'Guid', version: 4, beforeSend: hdr }),
        select: ['Id', 'Simbol', 'Nume'],
        sort: [{ selector: 'Simbol', desc: false }],
    });
    dsProiecte.value = new DataSource({
        store: new ODataStore({ url: AppSetting.API_Url_ComercialOdata + 'DOC_Proiecte', key: 'Id', keyType: 'Guid', version: 4, beforeSend: hdr }),
        select: ['Id', 'Cod', 'Nume'],
        sort: [{ selector: 'Cod', desc: false }],
        paginate: true,
        pageSize: 30,
    });
}

const partenerLabelHeader = computed(() => {
    // Tipcomanda=0 e o categorie validă ("Comercial Clienti" în nomenclatorul legacy) — nu se
    // poate testa cu `!Tipcomanda`, altfel 0 e tratat greșit ca "lipsă" (falsy) și eticheta
    // rămâne "Partener", ascunzând switch-ul "Livrare directă de la furnizor" de mai jos.
    if (headerDraft.value.Tipcomanda == null) return 'Partener';
    const cat = categoriiComenzi.value.find(c => c.TipComanda === headerDraft.value.Tipcomanda);
    return cat?.TipFurnizor === true ? 'Furnizor' : 'Client';
});

function buildDsParteneriHeader(tipFurnizor?: boolean | null) {
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const hdr = (e: any) => { e.headers = { Authorization: 'Bearer ' + token }; };
    const filter = tipFurnizor === true ? ['Furnizor', '=', true] : ['Client', '=', true];
    dsParteneriHeader.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_ComercialOdata + 'PAR_Parteneri',
            key: 'ID', keyType: 'Guid', version: 4, beforeSend: hdr,
        }),
        select: ['ID', 'Nume', 'CUI', 'ValutaID'],
        filter,
        sort: [{ selector: 'Nume', desc: false }],
        paginate: true,
        pageSize: 30,
    });
}

function onTipComandaHeaderChanged(e: any) {
    if (!e.value) { dsParteneriHeader.value = null; return; }
    const cat = categoriiComenzi.value.find(c => c.TipComanda === e.value);
    buildDsParteneriHeader(cat?.TipFurnizor);
    headerDraft.value.ClientId = null;
}

function onPartenerHeaderChanged(e: any) {
    if (!e.selectedItem) return;
    const valutaPartener = e.selectedItem.ValutaID ?? null;
    if (valutaPartener) headerDraft.value.ValutaId = valutaPartener;
}

function onEditHeader() {
    initEditHeaderDataSources();
    headerDraft.value = {
        ClientId: localOrderData.value?.ClientId ?? null,
        AgentId: localOrderData.value?.AgentId ?? null,
        ValutaId: localOrderData.value?.ValutaId ?? null,
        Tipcomanda: localOrderData.value?.Tipcomanda ?? null,
        DataComenzii: localOrderData.value?.DataComenzii ?? null,
        TermenLivrare: localOrderData.value?.TermenLivrare ?? null,
        Nume: localOrderData.value?.Nume ?? '',
        Observatii: localOrderData.value?.Observatii ?? '',
        ProiectId: localOrderData.value?.ProiectId ?? null,
        LivrareDirectaFurnizor: localOrderData.value?.LivrareDirectaFurnizor ?? false,
        ExpCurierNume: localOrderData.value?.ExpCurierNume ?? '',
    };
    const cat = categoriiComenzi.value.find(c => c.TipComanda === headerDraft.value.Tipcomanda);
    buildDsParteneriHeader(cat?.TipFurnizor);
    showEditHeaderPopup.value = true;
}

async function onSaveHeader() {
    isSavingHeader.value = true;
    try {
        await ProComenziService.patchComanda(props.comandaId, {
            ClientId: headerDraft.value.ClientId,
            AgentId: headerDraft.value.AgentId,
            ValutaId: headerDraft.value.ValutaId,
            Tipcomanda: headerDraft.value.Tipcomanda,
            DataComenzii: headerDraft.value.DataComenzii,
            TermenLivrare: headerDraft.value.TermenLivrare || null,
            Nume: headerDraft.value.Nume || null,
            Observatii: headerDraft.value.Observatii || null,
            ProiectId: headerDraft.value.ProiectId || null,
            LivrareDirectaFurnizor: partenerLabelHeader.value === 'Client' ? headerDraft.value.LivrareDirectaFurnizor : false,
            ExpCurierNume: headerDraft.value.ExpCurierNume || null,
        });
        await refreshLocalData();
        showEditHeaderPopup.value = false;
        toast('Comanda a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    } finally {
        isSavingHeader.value = false;
    }
}

function onCancelHeaderEdit() {
    showEditHeaderPopup.value = false;
}

// ── Tab activ documente ──
const activeDocTab = ref<string | null>(null);
function toggleDocTab(tab: string) {
    activeDocTab.value = activeDocTab.value === tab ? null : tab;
}

// ── Documente generate ──

// ── Rezervări (documente hold, temporare) ──
const rezervari = ref<any[]>([]);

async function refreshRezervari() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getRezervari(props.comandaId);
        rezervari.value = resp.data ?? [];
    } catch { rezervari.value = []; }
}

// ── Avize expediție ──
const avize = ref<any[]>([]);
const showGenereazaAvizPopup = ref(false);
const isGenereazaAviz = ref(false);

// Oglindă în frontend a blocajului din backend (GenereazaAviz: "există deja un aviz activ")
// — butonul se dezactivează proactiv, nu doar la eroarea din backend după click.
const existaAvizActiv = computed(() => avize.value.some((a: any) => !a.Anulat));
const avizFormData = ref({
    Data: new Date() as Date | null,
    GestiuneId: null as string | null,
    ExpediereNume: '',
    ExpediereMijloc: '',
    Observatii: '',
});

const dsGestiuni = new DataSource({
    store: new ODataStore({
        url: AppSetting.API_Url_DocumenteOdata + 'GES_Gestiuni',
        key: 'Id',
        keyType: 'Guid',
        version: 4,
        beforeSend: (e: any) => {
            e.headers = { Authorization: 'Bearer ' + (localStorage.getItem('ERA_Jwt') ?? '') };
        },
    }),
    select: ['Id', 'Nume'],
    sort: [{ selector: 'Nume', desc: false }],
});

async function refreshAvize() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getAvize(props.comandaId);
        avize.value = resp.data ?? [];
    } catch { avize.value = []; }
}

function onOpenGenereazaAvizPopup() {
    avizFormData.value = {
        Data: new Date(),
        GestiuneId: null,
        ExpediereNume: '',
        ExpediereMijloc: '',
        Observatii: '',
    };
    showGenereazaAvizPopup.value = true;
}

async function onConfirmGenereazaAviz() {
    isGenereazaAviz.value = true;
    try {
        const dto = {
            Data: avizFormData.value.Data,
            GestiuneId: avizFormData.value.GestiuneId ?? null,
            ExpediereNume: avizFormData.value.ExpediereNume || null,
            ExpediereMijloc: avizFormData.value.ExpediereMijloc || null,
            Observatii: avizFormData.value.Observatii || null,
        };
        const resp = await ProComenziService.genereazaAviz(props.comandaId, dto);
        const aviz = resp.data;
        showGenereazaAvizPopup.value = false;
        toast(`Aviz nr. ${aviz?.Numar ?? ''} generat cu succes.`, 'success', 3000);
        // Generarea avizului eliberează automat rezervarea (vezi ElibereazaRezervarile din backend)
        // — reîmprospătăm și tab-ul de Rezervări ca să reflecte imediat statusul "Anulată".
        await Promise.all([refreshAvize(), refreshRezervari(), refreshLocalData()]);
        // Comanda se finalizează automat DOAR când și factura (dacă e configurată pe categorie)
        // există deja activă — vezi FinalizeazaComandaDacaAreToateDocumentele din backend.
        if (localOrderData.value?.Stare !== 'Finalizata')
            toast('Comanda rămâne „În lucru" până se generează și factura.', 'info', 3500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isGenereazaAviz.value = false;
    }
}

// Nume fișier descărcat = Partener_NrDocument.pdf (fără caractere nepermise în nume de fișier).
function numeFisierRaport(numarDocument?: string | null): string {
    const partener = localOrderData.value?.Client?.Nume ?? 'Partener';
    const numar = numarDocument ?? '';
    return `${partener}_${numar}`.replace(/[\\/:*?"<>|]/g, '-').trim() + '.pdf';
}

function urlRaport(clasaRaport: string, documentID: string): string {
    const params = new URLSearchParams({ clasaRaport, documentID });
    return `${AppSetting.API_Url_Rapoarte}report/GetReportByClasaRaport?${params}`;
}

async function onTiparesteAviz(avizItem: any) {
    const clasaRaport: string | null | undefined = avizItem.Tip?.ClasaRaport;
    if (!clasaRaport) {
        toast('Tipul avizului nu are un raport configurat (ClasaRaport lipsă).', 'warning', 3000);
        return;
    }
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const ok = await previewReport(urlRaport(clasaRaport, avizItem.Id), token, numeFisierRaport(avizItem.Numar));
    if (!ok) toast('Raportul nu a putut fi generat.', 'error', 3000);
}


async function onAnuleazaAviz(avizItem: any) {
    const ok = await custom({
        title: 'Anulare aviz',
        messageHtml: `<div style="padding:4px 0">Ești sigur că vrei să anulezi avizul <b>${avizItem.Numar ?? ''}</b>?</div>`,
        buttons: [
            { text: 'Da, anulează', type: 'danger', onClick: () => true },
            { text: 'Renunță', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    try {
        await ProComenziService.anuleazaAviz(props.comandaId, avizItem.Id);
        toast('Avizul a fost anulat.', 'success', 2000);
        await Promise.all([refreshAvize(), refreshLocalData()]);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    }
}

// ── Recepții (NIR) ──
const receptii = ref<any[]>([]);
const showReceptiePopup = ref(false);
const isGenereazaReceptie = ref(false);
const receptieFormData = ref({
    Data: new Date() as Date | null,
    GestiuneId: null as string | null,
    Observatii: '',
});

interface ReceptieLinie {
    LinieId: string;
    ProdusNume: string;
    CantitateComandataOrig: number;
    CantitateAlreadyReceptionata: number;
    CantitateReceptionata: number;
    PretAchizitie: number | null;
}
const receptieLinii = ref<ReceptieLinie[]>([]);

async function refreshReceptii() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getReceptii(props.comandaId);
        receptii.value = resp.data ?? [];
    } catch { receptii.value = []; }
}

async function onOpenReceptiePopup() {
    receptieFormData.value = { Data: new Date(), GestiuneId: null, Observatii: '' };

    let cantitatiReceptionate: Record<string, number> = {};
    try {
        const resp = await ProComenziService.getCantitatiReceptionate(props.comandaId);
        cantitatiReceptionate = resp.data ?? {};
    } catch { /* ignore */ }

    receptieLinii.value = liniiGrid.value
        .filter((l: any) => !l.Anulata && l.ProdusID)
        .map((l: any) => {
            const alreadyReceived = cantitatiReceptionate[l.ID] ?? 0;
            const remaining = Math.max(0, (l.Cantitate ?? 0) - alreadyReceived);
            return {
                LinieId: l.ID,
                ProdusNume: l.ProdusNume ?? l.Produs?.Nume ?? '',
                CantitateComandataOrig: l.Cantitate ?? 0,
                CantitateAlreadyReceptionata: alreadyReceived,
                CantitateReceptionata: remaining,
                PretAchizitie: l.FurnizorPret ?? null,
            } as ReceptieLinie;
        });

    showReceptiePopup.value = true;
}

async function onConfirmReceptie() {
    const liniiDeTrimis = receptieLinii.value.filter(l => l.CantitateReceptionata > 0);
    if (liniiDeTrimis.length === 0) {
        toast('Introduceți cel puțin o cantitate recepționată.', 'warning', 3000);
        return;
    }
    isGenereazaReceptie.value = true;
    try {
        const dto = {
            Data: receptieFormData.value.Data,
            GestiuneId: receptieFormData.value.GestiuneId ?? null,
            Observatii: receptieFormData.value.Observatii || null,
            Linii: liniiDeTrimis.map(l => ({
                LinieId: l.LinieId,
                CantitateReceptionata: l.CantitateReceptionata,
                PretAchizitie: l.PretAchizitie ?? null,
            })),
        };
        const resp = await ProComenziService.genereazaReceptie(props.comandaId, dto);
        const nir = resp.data;
        showReceptiePopup.value = false;
        toast(`NIR nr. ${nir?.Numar ?? ''} generat cu succes.`, 'success', 3000);
        await Promise.all([refreshReceptii(), refreshRezervari(), refreshLocalData()]);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isGenereazaReceptie.value = false;
    }
}

async function onTiparesteNir(nirItem: any) {
    const clasaRaport: string | null | undefined = nirItem.Tip?.ClasaRaport;
    if (!clasaRaport) {
        toast('Tipul NIR-ului nu are un raport configurat (ClasaRaport lipsă).', 'warning', 3000);
        return;
    }
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const ok = await printReport(urlRaport(clasaRaport, nirItem.Id), token);
    if (!ok) toast('Raportul NIR nu a putut fi generat.', 'error', 3000);
}

// ── Facturi ──
const facturi = ref<any[]>([]);
const showGenereazaFacturaPopup = ref(false);
const isGenereazaFactura = ref(false);
const facturaFormData = ref({
    Data: new Date() as Date | null,
    Scadenta: null as Date | null,
    AvizId: null as string | null,
    TipFacturaId: null as string | null,
});

async function refreshFacturi() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getFacturi(props.comandaId);
        facturi.value = resp.data ?? [];
    } catch { facturi.value = []; }
}

const comenziStorno = ref<any[]>([]);

async function refreshComenziStorno() {
    if (!props.comandaId) return;
    try {
        const resp = await ProComenziService.getComenziStorno(props.comandaId);
        comenziStorno.value = resp.data ?? [];
    } catch { comenziStorno.value = []; }
}

function onOpenGenereazaFacturaPopup() {
    const primulAvizValid = avize.value.find((a: any) => !a.Anulat);
    facturaFormData.value = {
        Data: new Date(),
        Scadenta: null,
        AvizId: primulAvizValid?.Id ?? null,
        TipFacturaId: currentCategorie.value?.DOC_FCTipID ?? null,
    };
    showGenereazaFacturaPopup.value = true;
}

async function onConfirmGenereazaFactura() {
    isGenereazaFactura.value = true;
    try {
        const dto = {
            Data: facturaFormData.value.Data,
            Scadenta: facturaFormData.value.Scadenta ?? null,
            AvizId: facturaFormData.value.AvizId ?? null,
            TipFacturaId: facturaFormData.value.TipFacturaId ?? null,
        };
        const resp = await ProComenziService.genereazaFactura(props.comandaId, dto);
        const factura = resp.data;
        showGenereazaFacturaPopup.value = false;
        toast(`Factura nr. ${factura?.Numar ?? ''} generată cu succes.`, 'success', 3000);
        await Promise.all([refreshFacturi(), refreshLocalData()]);
        // Comanda se finalizează automat DOAR când și avizul (dacă e configurat pe categorie)
        // există deja activ — vezi FinalizeazaComandaDacaAreToateDocumentele din backend.
        if (localOrderData.value?.Stare !== 'Finalizata')
            toast('Comanda rămâne „În lucru" până se generează și avizul.', 'info', 3500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isGenereazaFactura.value = false;
    }
}

async function onStorneazaFactura(facturaItem: any) {
    const ok = await custom({
        title: 'Stornare factură',
        messageHtml: `<div style="padding:4px 0">Ești sigur că vrei să stornezi factura <b>${facturaItem.Numar ?? ''}</b>?</div>`,
        buttons: [
            { text: 'Da, stornează', type: 'danger', onClick: () => true },
            { text: 'Renunță', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    try {
        const resp = await ProComenziService.storneazaFactura(props.comandaId, facturaItem.Id);
        toast(`Storno nr. ${resp.data?.Numar ?? ''} generat.`, 'success', 3000);
        await Promise.all([refreshFacturi(), refreshLocalData()]);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    }
}

async function onAnuleazaFactura(facturaItem: any) {
    const ok = await custom({
        title: 'Anulare factură',
        messageHtml: `<div style="padding:4px 0">Ești sigur că vrei să anulezi factura <b>${facturaItem.Numar ?? ''}</b>? Acțiunea nu generează document de corecție și este ireversibilă.</div>`,
        buttons: [
            { text: 'Da, anulează', type: 'danger', onClick: () => true },
            { text: 'Renunță', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    try {
        await ProComenziService.anuleazaFactura(props.comandaId, facturaItem.Id);
        toast('Factura a fost anulată.', 'success', 2000);
        await Promise.all([refreshFacturi(), refreshLocalData()]);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    }
}

async function onTiparesteFactura(facturaItem: any) {
    const clasaRaport: string | null | undefined = facturaItem.Tip?.ClasaRaport;
    if (!clasaRaport) {
        toast('Tipul facturii nu are un raport configurat (ClasaRaport lipsă).', 'warning', 3000);
        return;
    }
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const ok = await previewReport(urlRaport(clasaRaport, facturaItem.Id), token, numeFisierRaport(facturaItem.Numar));
    if (!ok) toast('Factura nu a putut fi generată ca PDF.', 'error', 3000);
}

// Certificatul de garanție e un raport fix (ro.sold.erp.DOC_CertificatGarantie), independent de
// Tip.ClasaRaport al facturii — se generează din aceleași linii de document ca factura.
async function onTipareasteCertificatGarantie(facturaItem: any) {
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const fileName = `Certificat_Garantie_${facturaItem.Numar ?? ''}`.replace(/[\\/:*?"<>|]/g, '-').trim() + '.pdf';
    const ok = await previewReport(urlRaport('ro.sold.erp.DOC_CertificatGarantie', facturaItem.Id), token, fileName);
    if (!ok) toast('Certificatul de garanție nu a putut fi generat ca PDF.', 'error', 3000);
}

const documentsGenerate = computed(() => {
    // Avize, facturi și recepții (NIR) sunt sursa de adevăr pentru tipurile lor — au propriile
    // tab-uri cu acțiuni dedicate (tipărire, anulare/stornare). Tab-ul "Documente" rămâne doar
    // fallback pentru documente generate din comandă cu alt tip (ex: proformă, alt document
    // custom neconfigurat pe categorie) — nu mai duplică avize/facturi/NIR aici.
    const tipCoduriAcoperite = new Set(
        [...avize.value, ...facturi.value, ...receptii.value].map(d => d.Tip?.Cod).filter(Boolean)
    );

    const seen = new Set<string>();
    const docs: { DocumentID: string; Numar: string; TipCod: string; TipNume: string }[] = [];
    for (const l of liniiGrid.value) {
        const tipCod = l.Document?.Tip?.Cod;
        if (l.DocumentID && !seen.has(l.DocumentID) && !(tipCod && tipCoduriAcoperite.has(tipCod))) {
            seen.add(l.DocumentID);
            docs.push({
                DocumentID: l.DocumentID,
                Numar: l.Document?.Numar ?? '',
                TipCod: l.Document?.Tip?.Cod ?? '',
                TipNume: l.Document?.Tip?.Nume ?? '',
            });
        }
    }
    return docs;
});

function onOpenDocument(doc: any) {
    // Nume fix de fereastră — reutilizează același tab de browser la apeluri succesive;
    // Documente.vue adaugă documentul ca tab intern nou (vezi mounted() → query ?id=).
    window.open(`/documente?id=${doc.DocumentID}`, 'era_documente_tab');
}

const dsFurnizoriPO = shallowRef<DataSource | null>(null);

function buildDsFurnizoriPO() {
    const token = localStorage.getItem('ERA_Jwt') ?? '';
    const hdr = (e: any) => { e.headers = { Authorization: 'Bearer ' + token }; };
    dsFurnizoriPO.value = new DataSource({
        store: new ODataStore({
            url: AppSetting.API_Url_ComercialOdata + 'PAR_Parteneri',
            key: 'ID', keyType: 'Guid', version: 4, beforeSend: hdr,
        }),
        select: ['ID', 'Nume'],
        filter: ['Furnizor', '=', true],
        sort: [{ selector: 'Nume', desc: false }],
        paginate: true,
        pageSize: 30,
    });
}

// ── Adăugare rapidă linii ──
interface RapidaLinie {
    cod: string;
    produs: any | null;
    gasit: boolean;
    cantitate: number;
    pretUnitar: number | null;
    pretLista: number | null;
    pretListaValutaId: string | null;
    furnizorId: string | null;
    pretFurnizor: number | null;
    pretFurnizorValutaId: string | null;
}

const showAdaugareRapidaPopup = ref(false);
const rapida_coduri = ref('');
const rapida_linii = ref<RapidaLinie[]>([]);
const rapida_isProcessing = ref(false);
const rapida_isAdaugand = ref(false);
const rapidaTextareaRef = ref<HTMLTextAreaElement | null>(null);

const rapida_liniiGasite = computed(() => rapida_linii.value.filter(l => l.gasit));
const rapida_liniiNegasite = computed(() => rapida_linii.value.filter(l => !l.gasit));

let rapidaTimer: ReturnType<typeof setTimeout> | null = null;
watch(rapida_coduri, (val) => {
    if (!val.trim()) { rapida_linii.value = []; return; }
    if (rapidaTimer) clearTimeout(rapidaTimer);
    rapidaTimer = setTimeout(() => procesezaCoduri(), 500);
});

watch(showAdaugareRapidaPopup, (val) => {
    if (val) nextTick(() => rapidaTextareaRef.value?.focus());
});

function onDetaliiCellPrepared(e: any) {
    if (e.rowType !== 'header' || !e.column?.hint) return;
    if (e.cellElement.querySelector('.col-info-icon')) return;

    // Iconița se pune direct pe celula de header (nu pe .dx-datagrid-text-content,
    // care are overflow:hidden și ar ascunde-o pe coloanele înguste) — poziționată
    // absolut, rămâne mereu vizibilă indiferent de lățimea coloanei.
    e.cellElement.classList.add('header-with-info');

    const icon = document.createElement('i');
    icon.className = 'dx-icon-info col-info-icon';
    icon.addEventListener('mouseenter', (ev: Event) => showMetaTooltip(ev as MouseEvent, e.column.caption ?? '', e.column.hint));
    icon.addEventListener('mouseleave', hideMetaTooltip);
    e.cellElement.appendChild(icon);
}

// Evidențiere vizuală pentru liniile cu preț 0 — doar atenționare (poate fi intenționat: mostre,
// bonusuri etc.), nu blochează gridul; confirmarea explicită se cere la salvare, în onRowInserting/onRowUpdating.
function onDetaliiRowPrepared(e: any) {
    if (e.rowType !== 'data') return;
    const pret = e.data?.PretNegociat ?? e.data?.PretUnitar ?? 0;
    const arePretZero = !!e.data?.ProdusID && e.data?.Anulata !== true && Number(pret) === 0;
    e.rowElement.classList.toggle('row-pret-zero', arePretZero);
}

function onDetaliiContextMenuPreparing(e: any) {
    if (e.row?.rowType === 'header') return;
    const row = e.row?.data;
    const items: any[] = [];

    if (row?.ID) {
        items.push({
            text: 'Eșalonări livrare',
            icon: 'clock',
            onItemClick: () => onOpenEsalonari(row),
        });
    }

    if (row?.ProdusID && localOrderData.value?.ClientId) {
        items.push({
            text: 'Denumire partener',
            icon: 'user',
            onItemClick: () => onOpenDenumiriPersonalizate(row),
        });
    }

    if (!isReadOnly.value) {
        items.push({
            text: 'Import linii din Excel',
            icon: 'add',
            onItemClick: () => {
                rapida_coduri.value = '';
                rapida_linii.value = [];
                if (!dsFurnizoriPO.value) buildDsFurnizoriPO();
                showAdaugareRapidaPopup.value = true;
            },
        });
    }

    if (items.length > 0) e.items = items;
}

async function procesezaCoduri() {
    const coduri = rapida_coduri.value
        .split('\n')
        .map(c => c.trim())
        .filter(c => c.length > 0);
    if (coduri.length === 0) { rapida_linii.value = []; return; }
    rapida_isProcessing.value = true;
    try {
        // Deduplicate preserving original case
        const seenLower = new Set<string>();
        const unice: string[] = [];
        for (const cod of coduri) {
            if (!seenLower.has(cod.toLowerCase())) {
                seenLower.add(cod.toLowerCase());
                unice.push(cod);
            }
        }

        const filterStr = unice
            .map(c => `(Cod eq '${c.replace(/'/g, "''")}' or CodIntern eq '${c.replace(/'/g, "''")}')`)
            .join(' or ');

        const resp = await http.clientComercialOdata.get('PRO_Produse', {
            params: {
                '$filter': filterStr,
                '$select': 'Id,Cod,CodIntern,Nume,PretVanzare,Pret,TvaCurent,GarantieLuni,PretValutaId',
            },
        });
        const produse: any[] = resp.data?.value ?? [];

        rapida_linii.value = coduri.map(cod => {
            const produs = produse.find(p =>
                p.Cod?.toLowerCase() === cod.toLowerCase() ||
                p.CodIntern?.toLowerCase() === cod.toLowerCase()
            ) ?? null;
            return {
                cod,
                produs,
                gasit: !!produs,
                cantitate: 1,
                pretUnitar: produs ? (produs.PretVanzare ?? null) : null,
                pretLista: null,
                pretListaValutaId: produs?.PretValutaId ?? null,
                furnizorId: null,
                pretFurnizor: produs ? (produs.Pret ?? produs.PretVanzare ?? null) : null,
                pretFurnizorValutaId: produs?.PretValutaId ?? null,
            };
        });
    } catch {
        const coduri2 = rapida_coduri.value.split('\n').map(c => c.trim()).filter(c => c.length > 0);
        rapida_linii.value = coduri2.map(cod => ({ cod, produs: null, gasit: false, cantitate: 1, pretUnitar: null, pretLista: null, pretListaValutaId: null, furnizorId: null, pretFurnizor: null, pretFurnizorValutaId: null }));
    } finally {
        rapida_isProcessing.value = false;
    }
}

async function onAdaugaToate() {
    const gasite = rapida_liniiGasite.value;
    if (gasite.length === 0) return;
    rapida_isAdaugand.value = true;
    let adaugate = 0;
    try {
        for (const linie of gasite) {
            await ProComenziService.adaugaLinie({
                ComandaID: props.comandaId,
                ProdusID: linie.produs.Id,
                ProdusNume: linie.produs.Nume,
                Cantitate: linie.cantitate ?? 1,
                PretUnitar: linie.pretUnitar ?? 0,
                PretNegociat: linie.pretUnitar ?? 0,
                PretLista: linie.pretLista ?? null,
                PretListaValutaID: linie.pretListaValutaId ?? null,
                TVA: linie.produs.TvaCurent ?? 19,
                DiscountProc: 0,
                FurnizorID: linie.furnizorId ?? null,
                FurnizorPret: linie.pretFurnizor ?? null,
                FurnizorPretValutaID: linie.pretFurnizorValutaId ?? null,
                GarantieLuni: linie.produs.GarantieLuni ?? null,
            });
            adaugate++;
        }
        detaliiStore.value?.reload();
        refreshLocalData();
        toast(`${adaugate} linie(i) adăugate cu succes.`, 'success', 2000);
        rapida_coduri.value = '';
        rapida_linii.value = [];
        showAdaugareRapidaPopup.value = false;
    } catch (err: any) {
        if (adaugate > 0) {
            detaliiStore.value?.reload();
            refreshLocalData();
            toast(`${adaugate} din ${gasite.length} linii adăugate. ${getErrMsg(err)}`, 'warning', 5000);
        } else {
            toast(getErrMsg(err), 'error', 3000);
        }
    } finally {
        rapida_isAdaugand.value = false;
    }
}

// ── Grid CRUD ──
async function onRowInserting(e: any) {
    const data = { ...e.data };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);

    try {
        await ProComenziService.adaugaLinie({
            ComandaID: props.comandaId,
            ProdusID: data.ProdusID ?? null,
            ProdusNume: data.ProdusNume ?? null,
            Cantitate: data.Cantitate ?? 1,
            PretNegociat: data.PretNegociat ?? 0,
            PretUnitar: data.PretUnitar ?? data.PretNegociat ?? 0,
            PretLista: data.PretLista ?? null,
            PretListaValutaID: data.PretListaValutaID ?? null,
            TVA: data.TVA ?? 19,
            DiscountProc: data.DiscountProc ?? 0,
            TermenLivrare: data.TermenLivrare ?? null,
            FurnizorID: data.FurnizorID ?? null,
            FurnizorPret: data.FurnizorPret ?? null,
            FurnizorPretValutaID: data.FurnizorPretValutaID ?? null,
            GarantieLuni: data.GarantieLuni ?? null,
        });
        detaliiStore.value?.reload();
        refreshLocalData();
        toast('Linia a fost adăugată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onRowUpdating(e: any) {
    const key = e.key;
    const newData = { ...e.newData };
    e.cancel = true;
    setTimeout(() => gridRef.value?.instance?.cancelEditData(), 0);

    try {
        await ProComenziService.patchLinie(key, newData);
        detaliiStore.value?.reload();
        refreshLocalData();
        toast('Linia a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onRowRemoving(e: any) {
    e.cancel = true;
    const ok = await custom({
        title: 'Confirmare ștergere',
        messageHtml: 'Ești sigur că vrei să ștergi această linie?',
        buttons: [
            { text: 'Da', type: 'danger', onClick: () => true },
            { text: 'Nu', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    try {
        await ProComenziService.stergeLinie(e.key);
        detaliiStore.value?.reload();
        refreshLocalData();
        toast('Linia a fost ștearsă.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onStergeLiniiSelectate() {
    if (isReadOnly.value || liniiSelectate.value.length === 0) return;
    const count = liniiSelectate.value.length;
    const ok = await custom({
        title: 'Confirmare ștergere',
        messageHtml: `Ești sigur că vrei să ștergi <b>${count} linie(i) selectate</b>?`,
        buttons: [
            { text: 'Da', type: 'danger', onClick: () => true },
            { text: 'Nu', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    const ids = [...liniiSelectate.value];
    let errors = 0;
    for (const id of ids) {
        try {
            await ProComenziService.stergeLinie(id);
        } catch {
            errors++;
        }
    }
    liniiSelectate.value = [];
    gridRef.value?.instance?.clearSelection();
    detaliiStore.value?.reload();
    refreshLocalData();
    if (errors > 0) {
        toast(`${ids.length - errors} linie(i) șterse; ${errors} erori.`, 'warning', 3000);
    } else {
        toast(`${ids.length} linie(i) șterse.`, 'success', 1500);
    }
}

// ── Editare batch (toate liniile) ──

const hasUnsavedChanges = computed(() => {
    if (!isBatchEditMode.value) return false;
    const changes = gridRef.value?.instance?.option('editing.changes') ?? [];
    return (changes as any[]).length > 0;
});

watch(hasUnsavedChanges, (val) => {
    emit('unsaved-changes-change', val);
});

// Bug cunoscut devextreme-vue: la prima montare a acestui component într-o sesiune de
// navigare, widget-ul intern al gridului de linii poate rămâne neinițializat (gridRef.instance
// rămâne undefined) — orice montare ulterioară funcționează mereu corect. Nu există o cauză
// identificabilă din codul nostru (nicio eroare aruncată); comportamentul dispare la remontare.
// Detectăm eșecul și cerem părintelui (PRO_ComenziView) să ne remonteze o singură dată automat.
const gridInitFailedEmitted = ref(false);
let gridInitCheckScheduled = false;

function scheduleGridInitCheck() {
    if (gridInitCheckScheduled || gridInitFailedEmitted.value) return;
    gridInitCheckScheduled = true;
    setTimeout(() => {
        if (!gridRef.value?.instance && !gridInitFailedEmitted.value) {
            gridInitFailedEmitted.value = true;
            emit('grid-init-failed');
        }
    }, 800);
}

function onGridInitialized(e: any) {
    scheduleGridInitCheck();
    (e.component.element() as HTMLElement).addEventListener('focusout', onGridFocusOut);
}

watch(liniiReadOnly, (readOnly) => {
    if (!readOnly) {
        isBatchEditMode.value = true;
    } else {
        gridRef.value?.instance?.cancelEditData();
        isBatchEditMode.value = false;
        calcCache.value = {};
    }
}, { immediate: true });

function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (hasUnsavedChanges.value) {
        e.preventDefault();
        e.returnValue = '';
    }
}

onBeforeRouteLeave(async (_to, _from, next) => {
    if (!hasUnsavedChanges.value) { next(); return; }
    const ok = await custom({
        title: 'Modificări nesalvate',
        messageHtml: 'Aveți modificări nesalvate. Sigur doriți să părăsiți pagina?',
        buttons: [
            { text: 'Rămân pe pagină', stylingMode: 'outlined', onClick: () => false },
            { text: 'Părăsesc pagina', type: 'danger', onClick: () => true },
        ],
    }).show();
    ok ? next() : next(false);
});

// Enter în grid apelează întotdeauna "Salvează tot" — singura protecție e împotriva
// interacțiunii cu dropdown-ul de produs, unde Enter-ul e pentru căutare/selecție, nu pentru
// salvare. Interogăm starea REALĂ a dropdown-ului (.option('opened')) în loc de un flag propriu
// (isProdusDropdownOpen) — evenimentul "closed" nu se declanșează fiabil la close() programatic,
// ceea ce lăsa flag-ul blocat pe true și bloca definitiv Enter-ul după prima selecție de produs.
function onGridKeyDown(e: any) {
    if (e.event?.key !== 'Enter') return;
    if (currentDropBoxInstance?.option('opened')) return;
    setTimeout(() => {
        if (currentDropBoxInstance?.option('opened')) return;
        onSalveazaTot();
    }, 0);
}

const isSalvandTot = ref(false);

async function onSalveazaTot() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    const changes = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: string; data: any }>;

    if (changes.length === 0) {
        instance.cancelEditData();
        return;
    }

    isSalvandTot.value = true;
    try {
    let errors = 0;
    // "Refresh curs" se declanșează automat doar dacă vreo linie salvată implică o valută:
    // linie nouă cu preț de listă în valută, sau linie existentă unde s-a schimbat valuta.
    // Restul editărilor (cantitate, discount, observații etc.) nu ating cursul valutar.
    let necesitaRefreshCurs = false;
    for (const change of changes) {
        if (change.type === 'update') {
            const patchData = { ...change.data };
            // Pret Negociat nu se trimite niciodată — se derivă mereu pe backend din
            // Pret Unitar − Discount % (CalculeazaLinie). Pret Lista NU se mai forțează la
            // null când se editează Pret Unitar — backend-ul recalculează din Pret Lista
            // doar dacă acesta a fost efectiv modificat în acest PATCH (vezi UpdateDetaliu).
            delete patchData.PretNegociat;
            if ('PretListaValutaID' in patchData) necesitaRefreshCurs = true;
            try {
                await ProComenziService.patchLinie(change.key, patchData);
            } catch (err: any) {
                errors++;
                toast(getErrMsg(err), 'error', 3000);
            }
        } else if (change.type === 'insert') {
            const d = change.data;
            if (d.PretListaValutaID) necesitaRefreshCurs = true;
            try {
                await ProComenziService.adaugaLinie({
                    ComandaID: props.comandaId,
                    ProdusID: d.ProdusID ?? null,
                    ProdusNume: d.ProdusNume ?? null,
                    Cantitate: d.Cantitate ?? 1,
                    PretUnitar: d.PretUnitar ?? d.PretNegociat ?? 0,
                    PretLista: d.PretLista ?? null,
                    PretListaValutaID: d.PretListaValutaID ?? null,
                    TVA: d.TVA ?? 19,
                    DiscountProc: d.DiscountProc ?? 0,
                    TermenLivrare: d.TermenLivrare ?? null,
                    FurnizorID: d.FurnizorID ?? null,
                    FurnizorPret: d.FurnizorPret ?? null,
                    FurnizorPretValutaID: d.FurnizorPretValutaID ?? null,
                    GarantieLuni: d.GarantieLuni ?? null,
                });
            } catch (err: any) {
                errors++;
                toast(getErrMsg(err), 'error', 3000);
            }
        } else if (change.type === 'remove') {
            try {
                await ProComenziService.stergeLinie(change.key);
            } catch (err: any) {
                errors++;
                toast(getErrMsg(err), 'error', 3000);
            }
        }
    }

    instance.cancelEditData();
    calcCache.value = {};
    await instance.getDataSource()?.reload();
    await refreshLocalData();
    await Promise.all([refreshCantitatiRezervate(), refreshStocuriProduse()]);
    if (errors === 0 && necesitaRefreshCurs) await onRefreshCurs();
    instance.repaint();
    isBatchEditMode.value = !liniiReadOnly.value;
    if (errors === 0) toast('Modificările au fost salvate.', 'success', 1500);
    } finally {
        isSalvandTot.value = false;
    }
}

// ── Calcul instant coloane derivate (batch si row edit) ──
// NOTĂ: DevExtreme DxDataGrid nu are un eveniment "cellValueChanged" — varianta anterioară
// (@cell-value-changed) nu se declanșa niciodată. Interceptăm editoarele prin onEditorPreparing
// (pattern oficial DevExtreme pentru recalcul live), + separat pe coloana cu edit-cell-template
// custom (Valuta Pret Lista), care nu trece deloc prin onEditorPreparing.
const CALC_TRIGGER_FIELDS = ['Cantitate', 'PretUnitar', 'DiscountProc', 'TVA', 'PretLista', 'PretListaCurs'];

function onEditorPreparing(e: any) {
    if (e.parentType !== 'dataRow' || !CALC_TRIGGER_FIELDS.includes(e.dataField)) return;

    const defaultValueChangeHandler = e.editorOptions.onValueChanged;
    e.editorOptions.onValueChanged = function (args: any) {
        defaultValueChangeHandler(args);
        recalculeazaLinieLive({
            dataField: e.dataField,
            rowData: e.row?.data,
            rowKey: e.row?.key,
            rowIndex: e.row?.rowIndex,
            newValue: args.value,
        });
    };
}

async function recalculeazaLinieLive(params: { dataField: string; rowData: any; rowKey: any; rowIndex: number; newValue: any }) {
    const { dataField, rowData, rowKey, rowIndex, newValue } = params;
    const instance = gridRef.value?.instance;
    if (!instance || rowKey == null) return;

    const allChanges = (instance.option('editing.changes') ?? []) as Array<{ type: string; key: any; data: any }>;
    const rowChange = allChanges.find(c => c.key === rowKey);
    const currentData = {
        ...rowData,
        ...(rowChange?.data ?? {}),
        // editing.changes poate să nu reflecte încă valoarea tocmai introdusă în acest eveniment —
        // suprascriem explicit câmpul curent ca să nu calculăm pe date vechi.
        [dataField]: newValue,
    };

    // Când valuta prețului de listă se schimbă fără un PretLista ca bază de recalcul,
    // resetăm prețul unitar — nu există curs explicit pentru a converti un preț manual
    if (dataField === 'PretListaValutaID') {
        const hasPretLista = currentData.PretLista != null && Number(currentData.PretLista) > 0;
        if (!hasPretLista) {
            instance.cellValue(rowIndex, 'PretUnitar', null);
            instance.cellValue(rowIndex, 'PretNegociat', null);
            calcCache.value = { ...calcCache.value, [rowKey]: null };
            toast(
                'Valuta prețului de listă s-a schimbat. Prețul unitar a fost resetat — introduceți prețul manual.',
                'warning', 5000
            );
            return;
        }
    }

    // Dacă utilizatorul editează manual Pret Unitar, Pret Lista rămâne neschimbat
    // (informație de referință) — doar semnalăm backend-ului să nu-l mai recalculeze
    // din Pret Lista × Curs Pret Lista.
    const pretUnitarEditatManual = dataField === 'PretUnitar';

    const pretUnitarEfectiv = currentData.PretUnitar;

    try {
        const result = await ProComenziService.calculaLinie({
            PretUnitarEditatManual: pretUnitarEditatManual,
            Cantitate: currentData.Cantitate,
            PretUnitar: pretUnitarEfectiv,
            TVA: currentData.TVA,
            DiscountProc: currentData.DiscountProc,
            PretLista: currentData.PretLista,
            PretListaCurs: currentData.PretListaCurs,
            PretListaValutaID: currentData.PretListaValutaID ?? null,
        });
        calcCache.value = { ...calcCache.value, [rowKey]: result.data };
        // Pret Negociat = Pret Unitar − Pret Unitar × Discount% — întotdeauna calculat de backend
        // Îl propagăm în batch changes ca să fie salvat corect (coloana e read-only în grid)
        if (result.data?.PretNegociat != null)
            instance.cellValue(rowIndex, 'PretNegociat', result.data.PretNegociat);
        // Când Pret Lista + curs există, backend-ul poate recalcula și Pret Unitar — propagăm și pe acesta
        if (result.data?.PretUnitar != null)
            instance.cellValue(rowIndex, 'PretUnitar', result.data.PretUnitar);
        // Când valuta se schimbă și PretLista există, actualizează și cursul + notifică
        if (dataField === 'PretListaValutaID') {
            if (result.data?.PretListaCurs != null)
                instance.cellValue(rowIndex, 'PretListaCurs', result.data.PretListaCurs);
            if (result.data?.PretLista != null)
                instance.cellValue(rowIndex, 'PretLista', result.data.PretLista);
            if (result.data?.PretUnitar != null) {
                const curs = result.data?.PretListaCurs;
                const cursText = curs ? ` (curs: ${formatRO(curs)})` : '';
                toast(
                    `Prețul unitar recalculat: ${formatMoney(result.data.PretUnitar)}${cursText}`,
                    'info', 4000
                );
            }
        }
    } catch (err: any) {
        toast(`Recalcularea live a eșuat: ${getErrMsg(err)}`, 'error', 4000);
    }
}

// ── Selecție produs din dropdown cu coloane ──
let currentDropBoxInstance: any = null;
const innerProdusGridRef = ref<InstanceType<typeof DxDataGrid> | null>(null);
let shouldFocusFilter = false;

function onDropBoxOpened(e: any) {
    currentDropBoxInstance = e.component;
    shouldFocusFilter = true;
}

function onDropBoxClosed() {
    // currentDropBoxInstance rămâne — mai e nevoie de el pentru option('value')/close() ulterior
}

// Următoarea coloană editabilă, în ordinea VIZIBILĂ curentă a gridului (respectă coloanele
// activate din Column Chooser și reordonările făcute de utilizator) — nu o poziție fixă.
function getAdjacentEditableColumn(instance: any, fromDataField: string, direction: 1 | -1): string | null {
    const cols = ((instance?.getVisibleColumns?.() ?? []) as any[])
        .filter(c => c.visible !== false && c.allowEditing !== false && c.dataField);
    const idx = cols.findIndex(c => c.dataField === fromDataField);
    if (idx === -1) return null;
    const next = cols[idx + direction];
    return next?.dataField ?? null;
}

// Enter deschide dropdown-ul de căutare; Tab sare la următoarea coloană editabilă vizibilă —
// altfel Tab ar naviga mai întâi pe butonul "clear" (X) al DxDropDownBox, blocând parcurgerea
// cu Tab a coloanelor rândului (inclusiv cele afișate ulterior din Column Chooser).
function onProdusBoxKeyDown(e: any, cellInfo: any) {
    if (e.event.key === 'Enter') {
        e.event.stopPropagation();
        e.event.preventDefault();
        e.component.open();
        return;
    }
    if (e.event.key === 'Tab') {
        e.event.preventDefault();
        const instance = gridRef.value?.instance;
        if (!instance) return;
        const nextField = getAdjacentEditableColumn(instance, 'ProdusID', e.event.shiftKey ? -1 : 1);
        if (nextField) instance.editCell(cellInfo.rowIndex, nextField);
    }
}

function onProdusGridContentReady() {
    if (!shouldFocusFilter) return;
    shouldFocusFilter = false;
    window.setTimeout(() => {
        const el = (innerProdusGridRef.value as any)?.$el as HTMLElement | undefined;
        const input = el?.querySelector<HTMLInputElement>('.dx-datagrid-filter-row .dx-texteditor-input');
        input?.focus();
    }, 0);
}

function onProdusGridKeyDown(e: any, cellInfo: any) {
    if (e.event.key !== 'Enter' && e.event.key !== 'Tab') return;
    // Oprim mereu propagarea unui Enter din interiorul dropdown-ului de produs — altfel,
    // apăsat în rândul de filtru (căutare), scapă către grid-ul principal și poate declanșa
    // salvarea prematură a rândului nou aflat încă în curs de completare (vezi onGridKeyDown).
    e.event.stopPropagation();
    if (e.event.target?.closest?.('.dx-datagrid-filter-row')) return;
    e.event.preventDefault();
    const grid = e.component;
    const focusedRowIndex = grid.option('focusedRowIndex');
    if (focusedRowIndex < 0) return;
    const rowData = grid.getVisibleRows()[focusedRowIndex]?.data;
    if (rowData) onProdusGridSelection({ selectedRowsData: [rowData] }, cellInfo);
}

function onProdusGridSelection(e: any, cellInfo: any) {
    const prod = e.selectedRowsData[0];
    if (!prod) return;
    cellInfo.data.ProdusNume = prod.Nume;
    // Populează navigation property ca gridul să afișeze Cod/CodIntern fără reload
    cellInfo.data.Produs = { Cod: prod.Cod, CodIntern: prod.CodIntern, Nume: prod.Nume, Pret: prod.Pret, PretValuta: prod.PretValuta };

    // prod.Pret == null înseamnă "nesetat"; 0 e o valoare de preț validă (produs gratuit),
    // nu se tratează ca "gol" — se distinge explicit de null.
    const pretLista = prod.Pret;
    const cursLista = prod.PretValuta?.Curs ?? null;
    if (pretLista != null && cursLista != null && cursLista > 0) {
        // Pret Unitar = Pret Lista × curs — Pret Lista rămâne populat pentru referință/Refresh curs
        cellInfo.data.PretLista = pretLista;
        cellInfo.data.PretListaCurs = cursLista;
        cellInfo.data.PretUnitar = Math.round(pretLista * cursLista * 100) / 100;
    } else {
        // Produsul nu are preț de listă în valută — folosim direct prețul de vânzare
        cellInfo.data.PretLista = null;
        cellInfo.data.PretListaCurs = null;
        cellInfo.data.PretUnitar = prod.PretVanzare ?? 0;
    }
    cellInfo.data.PretNegociat = cellInfo.data.PretUnitar;
    if (prod.PretValutaId != null) {
        cellInfo.data.PretListaValutaID = prod.PretValutaId;
        cellInfo.data.FurnizorPretValutaID = prod.PretValutaId;
    }
    // Preț Furnizor inițial = Pret (cost achiziție) dacă există, altfel PretVanzare
    const pretFurnizor = prod.Pret || prod.PretVanzare || null;
    if (pretFurnizor != null) cellInfo.data.FurnizorPret = pretFurnizor;
    const cat = currentCategorie.value;
    // TVA: dacă TvaDinComanda e activ, se folosește TVA-ul categoriei; altfel TVA-ul produsului, cu fallback pe categorie sau 19%
    const catTVA = cat?.TVA ?? null;
    cellInfo.data.TVA = cat?.TvaDinComanda ? (catTVA ?? 19) : (prod.TvaCurent ?? catTVA ?? 19);
    // Garanție: produsul are prioritate, then categorie
    cellInfo.data.GarantieLuni = prod.GarantieLuni ?? cat?.GarantieLuni ?? null;
    cellInfo.setValue(prod.Id);
    // Forțează actualizarea afișajului în DxDropDownBox
    currentDropBoxInstance?.option('value', prod.Id);
    currentDropBoxInstance?.close();
    // Mută focusul pe Cantitate folosind API-ul DevExtreme
    window.setTimeout(() => {
        gridRef.value?.instance?.editCell(cellInfo.rowIndex, 'Cantitate');
    }, 100);
}

// ── Export PDF (Bon Comandă) ──
async function onExportPdf() {
    if (!gridRef.value?.instance) return;
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageW = 297;
    const margin = 10;
    const contentW = pageW - 2 * margin;
    const order = localOrderData.value;
    const valuta = order?.Valuta?.Simbol ?? 'RON';

    // ── Titlu ──────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('BON COMANDĂ', margin, 13);

    // Nr. + Data + Stare (aliniate dreapta sus)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Nr.: ${order?.Cod ?? ''}`, margin + 90, 10);
    doc.text(`Data: ${formatDate(order?.DataComenzii)}`, margin + 90, 15);
    doc.text(`Stare: ${getStareLabel(order?.Stare)}`, margin + 90, 20);

    // ── Separator 1 ────────────────────────────────────────────────────────
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, 23, pageW - margin, 23);

    // ── Info partener / agent ──────────────────────────────────────────────
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Client:', margin, 28);
    doc.setFont('helvetica', 'normal');
    doc.text(order?.Client?.Nume ?? '—', margin + 16, 28);
    if (order?.Client?.CUI) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(`CUI: ${order.Client.CUI}`, margin + 16, 32);
        doc.setTextColor(0, 0, 0);
    }

    const colRight = margin + contentW / 2;
    doc.setFont('helvetica', 'bold');
    doc.text('Agent:', colRight, 28);
    doc.setFont('helvetica', 'normal');
    doc.text(order?.Agent?.Nume ?? '—', colRight + 14, 28);

    if (order?.TermenLivrare) {
        doc.setFont('helvetica', 'bold');
        doc.text('Termen:', colRight, 32);
        doc.setFont('helvetica', 'normal');
        doc.text(formatDate(order.TermenLivrare), colRight + 17, 32);
    }

    if (order?.Observatii) {
        doc.setFont('helvetica', 'bold');
        doc.text('Obs.:', margin, 36);
        doc.setFont('helvetica', 'normal');
        const obsText = order.Observatii.length > 100 ? order.Observatii.substring(0, 100) + '…' : order.Observatii;
        doc.text(obsText, margin + 12, 36);
    }

    // ── Totaluri ───────────────────────────────────────────────────────────
    const totY = 40;
    doc.setFillColor(248, 249, 252);
    doc.rect(margin, totY - 4, contentW, 7, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.rect(margin, totY - 4, contentW, 7, 'D');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(`Val. fără TVA: ${formatMoney(order?.ValoareComanda)} ${valuta}`, margin + 4, totY);
    doc.text(`TVA: ${formatMoney(order?.Tva)} ${valuta}`, margin + contentW * 0.38, totY);
    doc.text(`TOTAL: ${formatMoney(order?.TotalComanda)} ${valuta}`, margin + contentW * 0.63, totY);

    // ── Linii comenzii (DevExtreme grid) ──────────────────────────────────
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    await exportDataGrid({
        jsPDFDocument: doc,
        component: gridRef.value.instance,
        topLeft: { x: margin, y: 48 },
        customizeCell({ pdfCell, gridCell }: any) {
            pdfCell.fontSize = 7.5;
            if (gridCell?.rowType === 'header') {
                pdfCell.font = { style: 'bold' };
                pdfCell.backgroundColor = '#f1f5f9';
            }
        },
    });

    doc.save(`Comanda_${order?.Cod ?? 'export'}.pdf`);
}

// ── Rezervări ──
async function onRezervaToate() {
    isRezervandToate.value = true;
    try {
        await ProComenziService.rezervaToate(props.comandaId);
        await Promise.all([refreshCantitatiRezervate(), refreshStocuriProduse(), refreshRezervari()]);
        gridRef.value?.instance?.repaint();
        toast('Cantitățile disponibile au fost rezervate.', 'success', 2500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3500);
    } finally {
        isRezervandToate.value = false;
    }
}

function onOpenRezerveLinie(row: any) {
    rezervaLineId.value = row.ID;
    rezervareLinieProdusNume.value = row.Produs?.Nume || row.ProdusNume || 'Produs';
    rezervareLinieProdusId.value = row.ProdusID ?? '';
    rezervareLinieCantitateMax.value = row.Cantitate ?? 0;
    rezervareLinieCantitate.value = cantitatiRezervate.value[row.ID] ?? 0;
    showRezervareLiniePopup.value = true;
}

function onRezervareLiniePopupShown() {
    rezervareCantitateRef.value?.instance?.focus();
    rezervareCantitateRef.value?.instance?.element()?.querySelector('input')?.select();
}

const rezervareLinieStocDisponibil = computed(() => {
    return stocuriProduse.value[rezervareLinieProdusId.value]?.StocRamas ?? null;
});

async function onConfirmRezerveLinie() {
    showRezervareLiniePopup.value = false;
    try {
        await ProComenziService.rezervaLinie(props.comandaId, rezervaLineId.value, rezervareLinieCantitate.value);
        await Promise.all([refreshCantitatiRezervate(), refreshStocuriProduse(), refreshRezervari()]);
        gridRef.value?.instance?.repaint();
        toast('Rezervarea a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3500);
    }
}

function formatRezervare(id: string | undefined, cantitate: number | undefined): string {
    if (!id) return '—';
    const rez = cantitatiRezervate.value[id] ?? 0;
    const cant = cantitate ?? 0;
    if (cant === 0) return '—';
    return `${cant} / ${rez}`;
}

function getRezervareClass(id: string | undefined, cantitate: number | undefined): string {
    if (!id) return 'rezervare-none';
    const rez = cantitatiRezervate.value[id] ?? 0;
    const cant = cantitate ?? 0;
    if (cant === 0 || rez === 0) return 'rezervare-none';
    return rez >= cant ? 'rezervare-full' : 'rezervare-partial';
}

// ── Helpers ──
function toast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', displayTime = 3000) {
    notify({ message, type, displayTime }, { position: 'bottom right', direction: 'up-push' });
}

function getErrMsg(err: any): string {
    if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') return data;
        if (data.error?.message) return data.error.message; // OData format
        if (data.message) return data.message; // PermissionFilter (403 acces refuzat)
        if (data.Message) return data.Message; // Classic ASP.NET
        if (data.ExceptionMessage) return data.ExceptionMessage;
        if (data.title) return data.title; // ASP.NET Core ProblemDetails
    }
    return err?.message ?? 'A apărut o eroare.';
}

async function refreshLocalData() {
    if (!props.comandaId) return;
    try {
        const resp = await http.clientComercialOdata.get(
            `PRO_Comenzi(${props.comandaId})?$expand=Agent($select=Nume),Client($select=Nume,CUI),Valuta($select=Simbol),Proiect($select=Cod,Nume)`
        );
        localOrderData.value = resp.data;
        checkRefreshCursNecesar();
    } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('refreshLocalData a eșuat — header-ul comenzii (partener/agent/valută/proiect) poate rămâne cu date vechi sau incomplete:', getErrMsg(err));
    }
}

const STARE_COLORS: Record<string, { background: string; color: string }> = {
    'inasteptare': { background: '#f5f3ff', color: '#6d28d9' },
    'confirmata':  { background: '#eff6ff', color: '#1d4ed8' },
    'inlucru':     { background: '#fef3c7', color: '#92400e' },
    'finalizata':  { background: '#ecfdf5', color: '#065f46' },
    'anulata':     { background: '#fef2f2', color: '#991b1b' },
};

const STARE_LABELS: Record<string, string> = {
    'InAsteptare': 'In Asteptare',
    'InLucru':     'In Lucru',
};
function getStareLabel(stare: string | null | undefined): string {
    return STARE_LABELS[stare ?? ''] ?? (stare ?? '');
}

function getStareChipStyle(stare: string): Record<string, string> {
    const key = (stare ?? '').toLowerCase().trim();
    const t = STARE_COLORS[key] ?? { background: '#f1f5f9', color: '#475569' };
    return {
        background: t.background, color: t.color,
        padding: '4px 12px', borderRadius: '20px', fontWeight: '700',
        fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
        display: 'inline-block', whiteSpace: 'nowrap',
    };
}

function formatDate(val: string | null | undefined): string {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatRO(val: number | null | undefined): string {
    if (val == null) return '';
    return val.toLocaleString('ro-RO', { maximumFractionDigits: 10 });
}

// Folosit doar pe "Total cu TVA" — mereu exact 2 zecimale (12.20, nu 12,2), spre deosebire de
// formatRO (folosit pe restul coloanelor numerice), care taie zecimalele finale.
function formatFixed2(val: number | null | undefined): string {
    if (val == null) return '';
    return val.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoney(val: number | null | undefined): string {
    if (val == null) return '—';
    return val.toLocaleString('ro-RO', { maximumFractionDigits: 10 });
}

function formatMoney2(val: number | null | undefined): string {
    if (val == null) return '—';
    return val.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getProdusNume(rowData: any): string {
    return rowData?.Produs?.Nume || rowData?.ProdusNume || '';
}

// ProdusID e Guid — filtrarea/căutarea nu se poate face pe el direct (eroare OData
// "Edm.Guid"/"Edm.String" la operatorul Equal); redirecționăm filtrul către coloanele
// text din navigarea Produs, deja aduse prin $expand.
function produsFilterExpression(value: any, operation: string) {
    return [
        ['Produs.Nume', operation, value],
        'or',
        ['Produs.Cod', operation, value],
        'or',
        ['Produs.CodIntern', operation, value],
    ];
}

// Denumirea din dropdown-ul de căutare produs — căutare pe cuvinte, nu pe fraza exactă:
// "telefon ihunt" trebuie să găsească și "telefon mobil ihunt" (ordinea cuvintelor nu contează),
// deci fiecare cuvânt introdus trebuie să apară undeva în Nume, ca o condiție AND separată.
function produsNumeFilterExpression(value: any, operation: string) {
    if (typeof value !== 'string' || !value.trim()) return ['Nume', operation, value];

    const cuvinte = value.trim().split(/\s+/);
    if (cuvinte.length === 1) return ['Nume', operation, value];

    const conditii: any[] = [];
    cuvinte.forEach((cuvant, i) => {
        if (i > 0) conditii.push('and');
        conditii.push(['Nume', operation, cuvant]);
    });
    return conditii;
}

function getProdusLookupDisplay(item: any): string {
    if (!item) return '';
    const cod = [item.Cod, item.CodIntern].filter(Boolean).join(' / ');
    return cod ? `${cod} — ${item.Nume ?? ''}` : (item.Nume ?? '');
}

function calcCursLista(row: any): number | null {
    // Afișează mereu ultimul curs BNR din ERP_CursValutar pentru valuta liniei — nu cursul
    // "înghețat" la ultimul calcul (PretListaCurs) și nici ERP_Valute.Curs (poate fi neactualizat).
    const valutaId = row.PretListaValutaID ? String(row.PretListaValutaID).toLowerCase() : null;
    if (valutaId && latestCursuriByValuta.value[valutaId] != null) {
        return latestCursuriByValuta.value[valutaId];
    }
    return row.PretListaCurs ?? row.PretListaValuta?.Curs ?? null;
}


function getPretUnitarDisplay(cellData: any): number | null {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as any[];
    const rowChange = changes.find(c => c.key === cellData.key);
    if (rowChange?.data?.PretUnitar != null) return rowChange.data.PretUnitar;
    return calcCache.value[cellData.key]?.PretUnitar ?? cellData.data.PretUnitar;
}

function getPretNegociatDisplay(cellData: any): number | null {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as any[];
    const rowChange = changes.find(c => c.key === cellData.key);
    if (rowChange?.data?.PretNegociat !== undefined) return rowChange.data.PretNegociat;
    return calcCache.value[cellData.key]?.PretNegociat ?? cellData.data.PretNegociat;
}

function calcStocEfectiv(row: any): number | null {
    if (!row.ProdusID) return null;
    return stocuriProduse.value[row.ProdusID]?.StocEfectiv ?? null;
}

function calcStocRezervat(row: any): number | null {
    if (!row.ProdusID) return null;
    return stocuriProduse.value[row.ProdusID]?.StocRezervat ?? null;
}


function calcStocRamas(row: any): number | null {
    if (!row.ProdusID) return null;
    return stocuriProduse.value[row.ProdusID]?.StocRamas ?? null;
}

// ── Eșalonări CRUD ──
function onOpenEsalonari(row: any) {
    if (!row?.ID) return;
    esalonariDetaliuId.value = row.ID;
    esalonariDetaliuNume.value = row.Produs?.Nume ?? row.ProdusNume ?? 'Linie';

    const token = localStorage.getItem('ERA_Jwt') ?? '';
    esalonariStore.value = new DataSource({
        store: {
            type: 'odata',
            url: `${AppSetting.API_Url_ComercialOdata}PRO_ComenziEsalonari?detaliuId=${row.ID}`,
            key: 'Id',
            version: 4,
            beforeSend: (e: any) => {
                e.headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };
            },
        },
    });

    showEsalonariPopup.value = true;
}

async function onEsalonareInserting(e: any) {
    const data = { ...e.data };
    e.cancel = true;
    setTimeout(() => esalonariGridRef.value?.instance?.cancelEditData(), 0);

    try {
        await ProComenziService.adaugaEsalonare({
            ComandaDetaliuID: esalonariDetaliuId.value!,
            DataLivrare: data.DataLivrare ?? null,
            Cantitate: data.Cantitate ?? 0,
            CantitateStoc: data.CantitateStoc ?? 0,
            CantitateTotal: data.CantitateTotal ?? data.Cantitate ?? 0,
            Status: data.Status ?? 1,
            CantitateDorita: data.CantitateDorita ?? null,
            CantitateDinNecesar: data.CantitateDinNecesar ?? null,
        });
        esalonariStore.value?.reload();
        toast('Eșalonarea a fost adăugată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onEsalonareUpdating(e: any) {
    const key = e.key;
    const newData = { ...e.newData };
    e.cancel = true;
    setTimeout(() => esalonariGridRef.value?.instance?.cancelEditData(), 0);

    try {
        await ProComenziService.patchEsalonare(key, newData);
        esalonariStore.value?.reload();
        toast('Eșalonarea a fost actualizată.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

async function onEsalonareRemoving(e: any) {
    e.cancel = true;
    const ok = await custom({
        title: 'Confirmare ștergere',
        messageHtml: 'Ești sigur că vrei să ștergi această eșalonare?',
        buttons: [
            { text: 'Da', type: 'danger', onClick: () => true },
            { text: 'Nu', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    try {
        await ProComenziService.stergeEsalonare(e.key);
        esalonariStore.value?.reload();
        toast('Eșalonarea a fost ștearsă.', 'success', 1500);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 3000);
    }
}

// ── Column chooser: închide la click în afară ──
function handleColumnChooserOutsideClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (
        !target.closest('.dx-datagrid-column-chooser') &&
        !target.closest('[data-name="columnChooserButton"]') &&
        !target.closest('.dx-column-chooser-button')
    ) {
        gridRef.value?.instance?.hideColumnChooser?.();
    }
}

// ── Salvare automată la ieșirea focusului din grid — pattern DevExtreme pentru batch mode.
// NU ne bazăm pe relatedTarget-ul evenimentului "focusout" (nesigur — multe butoane
// DevExtreme fac preventDefault() pe mousedown, deci relatedTarget poate fi null) și NICI
// pe instance.element() (referință posibil capturată prea devreme, înainte ca toolbar-ul
// să fie complet randat). Verificăm în schimb, după un delay, document.activeElement cu
// closest('.detalii-grid') — selector CSS, robust indiferent de referința instanței —
// și dăm suficient timp propriilor setTimeout-uri interne DevExtreme (ex. addRow() la
// click pe "Adaugă produs în comandă") să-și termine mutarea focusului înainte să verificăm.
function onGridFocusOut() {
    if (isSalvandTot.value) return;
    const instance = gridRef.value?.instance;
    if (!instance) return;

    setTimeout(() => {
        // Navigarea între tab-uri e gestionată explicit de PRO_ComenziView
        // (confirmSwitchAway — dialog Salvează/Renunță/Rămân) — dacă e activă verificarea
        // aia, nu salvăm silențios aici, altfel dialogul nu mai apucă să apară. Pentru
        // orice alt click în afara grid-ului (butoane, restul paginii), auto-salvarea
        // rămâne activă normal.
        if (props.suppressAutoSave) return;

        const changes = (instance.option('editing.changes') ?? []) as any[];
        if (changes.length === 0) return;

        const active = document.activeElement as HTMLElement | null;
        if (active?.closest('.detalii-grid')) return;
        if (active?.closest('.dx-overlay-wrapper')) return;
        onSalveazaTot();
    }, 150);
}

// ── Init ──
onMounted(async () => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('mousedown', handleColumnChooserOutsideClick);
    initEditHeaderDataSources();
    if (!dsFurnizoriPO.value) buildDsFurnizoriPO();

    try {
        const [catResp, drepturiResp] = await Promise.all([
            ProComenziService.getCategoriiPermise(),
            ProComenziService.getDrepturiMele(),
        ]);
        categoriiComenzi.value = catResp.data ?? [];
        if (drepturiResp.data) drepturi.value = { ...drepturi.value, ...drepturiResp.data };
    } catch { /* ignore */ }

    loadCursuriValutare();
    loadLatestCursuriToateValutele();
    loadTipuriFactura();
    checkRefreshCursNecesar();
});

onUnmounted(async () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('mousedown', handleColumnChooserOutsideClick);
    (gridRef.value?.instance?.element() as HTMLElement | undefined)?.removeEventListener('focusout', onGridFocusOut);
    if (connectedOrderId) await disconnectSignalR(connectedOrderId);
});

watch(() => props.orderData, (data) => {
    if (data && (!localOrderData.value || localOrderData.value.Id !== data.Id)) {
        localOrderData.value = { ...data };
    }
}, { immediate: true });

watch(() => props.comandaId, (id) => {
    if (!id) return;
    syncSignalRConnection(id).catch(() => {});
    isLoading.value = true;
    const token = localStorage.getItem('ERA_Jwt');
    if (!token) { isLoading.value = false; return; }

    detaliiStore.value = new DataSource({
        store: {
            type: 'odata',
            url: `${AppSetting.API_Url_ComercialOdata}PRO_ComenziDetalii?comandaId=${id}`,
            key: 'ID',
            keyType: 'Guid',
            version: 4,
            beforeSend: (e: any) => {
                e.headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };
                e.params.$expand = 'Produs($select=Cod,Nume,CodIntern,Pret,Stocabil;$expand=PretValuta($select=Curs)),Furnizor($select=Nume),FurnizorPretValuta($select=Simbol),PretListaValuta($select=Curs,Simbol),Document($select=Numar;$expand=Tip($select=Cod,Nume)),ClasificatieBugetara($select=Cod,Denumire)';
            },
        },
        sort: [{ selector: 'NrCrt', desc: false }],
    });

    detaliiStore.value.load()
        .then(() => { isLoading.value = false; })
        .catch(() => { isLoading.value = false; });

    refreshLocalData();
    refreshCantitatiRezervate();
    refreshStocuriProduse();
    refreshRezervari();
    refreshAvize();
    refreshReceptii();
    refreshFacturi();
    refreshComenziStorno();
}, { immediate: true });

const hasAutoOpenedRow = ref(false);

function onSelectionChanged(e: any) {
    liniiSelectate.value = (e.selectedRowKeys ?? []) as string[];
}

// Apelat din butonul "Refresh curs" — previzualizează câte produse ar avea Pret Unitar
// modificat (inclusiv cele ajustate manual) și cere confirmare înainte de a aplica efectiv.
async function onRefreshCursClick() {
    if (isRefreshingCurs.value) return;
    isRefreshingCurs.value = true;
    try {
        const preview = await ProComenziService.previewRefreshCurs(props.comandaId);
        const total = preview.data?.TotalModificate ?? 0;
        if (total === 0) {
            toast('Niciun preț unitar nu ar fi modificat — curs deja actualizat.', 'info', 3000);
            return;
        }
        const ok = await custom({
            title: 'Confirmare Refresh Curs',
            messageHtml: `<div style="padding:4px 0">Prețul unitar va fi modificat la <b>${total}</b> produs(e) (inclusiv cele ajustate manual).<br>Continui cu refresh curs?</div>`,
            buttons: [
                { text: 'Da', type: 'default', onClick: () => true },
                { text: 'Nu', stylingMode: 'outlined', onClick: () => false },
            ],
        }).show();
        if (!ok) return;
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
        return;
    } finally {
        isRefreshingCurs.value = false;
    }
    await onRefreshCurs();
}

// Verifică dacă butonul "Refresh curs" are vreun efect real — apelat după orice reîncărcare a
// comenzii (refreshLocalData), ca butonul să fie ascuns când niciun preț nu s-ar schimba.
async function checkRefreshCursNecesar() {
    if (!props.comandaId) { refreshCursNecesar.value = false; return; }
    try {
        const preview = await ProComenziService.previewRefreshCurs(props.comandaId);
        refreshCursNecesar.value = (preview.data?.TotalModificate ?? 0) > 0;
    } catch {
        refreshCursNecesar.value = false;
    }
}

async function onRefreshCurs() {
    if (isRefreshingCurs.value) return;
    isRefreshingCurs.value = true;
    try {
        const instance = gridRef.value?.instance;
        instance?.cancelEditData();
        await ProComenziService.refreshCurs(props.comandaId);
        calcCache.value = {};
        await detaliiStore.value?.reload();
        await refreshLocalData();
        await Promise.all([loadCursuriValutare(), loadLatestCursuriToateValutele()]);
        instance?.repaint();
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isRefreshingCurs.value = false;
    }
}

async function onOpenDenumiriPersonalizate(rowData: any) {
    const clientId = localOrderData.value?.ClientId;
    if (!clientId) {
        toast('Comanda nu are un partener selectat.', 'warning', 3000);
        return;
    }
    if (!rowData?.ProdusID) return;

    const produsId: string = rowData.ProdusID;
    const produsNume: string = rowData.ProdusNume ?? rowData.Produs?.Nume ?? '';
    const produsCod: string = rowData.Produs?.Cod ?? '';
    denumiriProdusNume.value = produsNume;
    showDenumiriPopup.value = true;

    try {
        const resp = await ProComenziService.getDenumiriProduse(clientId);
        const existente: any[] = resp.data?.value ?? [];
        const ex = existente.find((d: any) => d.ProdusID === produsId) ?? null;
        const partenerNume = localOrderData.value?.Client?.Nume ?? '';

        denumiriRows.value = [{
            ProdusID: produsId,
            ProdusNume: produsNume,
            ProdusCod: produsCod,
            PartenerNume: partenerNume,
            Cod: ex?.Cod ?? '',
            Nume: ex?.Nume ?? '',
            _existentId: ex?.Id ?? null,
            _originalCod: ex?.Cod ?? '',
            _originalNume: ex?.Nume ?? '',
        }];

        await nextTick();
        setTimeout(() => denumiriGridRef.value?.instance?.editRow(0), 100);
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    }
}

async function onSaveDenumiri() {
    if (isSavingDenumiri.value) return;
    const clientId = localOrderData.value?.ClientId;
    if (!clientId) return;

    await denumiriGridRef.value?.instance?.saveEditData();
    isSavingDenumiri.value = true;
    let salvate = 0;
    let erori = 0;

    for (const row of denumiriRows.value) {
        const hasChanged = row.Cod !== row._originalCod || row.Nume !== row._originalNume;
        if (!hasChanged) continue;

        const codTrim = (row.Cod ?? '').trim();
        const numeTrim = (row.Nume ?? '').trim();

        try {
            if (row._existentId) {
                if (!codTrim && !numeTrim) {
                    // Both cleared → delete
                    await ProComenziService.deleteDenumire(row._existentId);
                    row._existentId = null;
                } else {
                    await ProComenziService.updateDenumire(row._existentId, { Cod: codTrim, Nume: numeTrim });
                }
            } else if (codTrim || numeTrim) {
                const resp = await ProComenziService.saveDenumire({
                    PartenerID: clientId,
                    ProdusID: row.ProdusID,
                    Cod: codTrim,
                    Nume: numeTrim,
                });
                row._existentId = resp.data?.Id ?? null;
            }
            row._originalCod = codTrim;
            row._originalNume = numeTrim;
            salvate++;
        } catch {
            erori++;
        }
    }

    isSavingDenumiri.value = false;

    if (erori > 0) {
        toast(`Salvare parțială: ${salvate} OK, ${erori} erori.`, 'warning', 4000);
    } else {
        if (salvate > 0) toast('Denumire salvată.', 'success', 2000);
        showDenumiriPopup.value = false;
    }
}

async function onGenereazaStornoPartial() {
    if (isGenerandStorno.value || liniiSelectate.value.length === 0) return;
    const ok = await custom({
        title: 'Generare storno parțial',
        messageHtml: `<div style="padding:4px 0">Se generează storno pentru <b>${liniiSelectate.value.length} linie(i) selectate</b>.<br>Comanda storno va fi creată cu starea <b>Confirmată</b>.<br><br>Continui?</div>`,
        buttons: [
            { text: 'Da, generează', type: 'default', onClick: () => true },
            { text: 'Renunță', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;
    isGenerandStorno.value = true;
    try {
        const resp = await ProComenziService.genereazaStorno(props.comandaId, liniiSelectate.value);
        const storno = resp.data;
        gridRef.value?.instance?.clearSelection();
        toast(`Comandă storno generată: ${storno?.Cod ?? ''}`, 'success', 3000);
        await refreshComenziStorno();
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 4000);
    } finally {
        isGenerandStorno.value = false;
    }
}

// Coduri TipComanda pentru comenzi storno — aceleași ca în GridComenzi.vue/backend
// (TipComanda=9, "Comenzi Retururi către Furnizori", are TipFurnizor=true la fel ca o
// comandă furnizor normală — exclus explicit ca să nu fie "nimerit" din greșeală ca
// categorie de generare).
const TIPURI_STORNO_COMENZI_FURNIZORI = [3, 9];

// Portat din GridComenzi.vue (meniu contextual „Generează comenzi furnizori") — mutat aici
// ca buton, direct pe comanda deschisă, la cererea userului.
async function onGenereazaComenziFurnizori() {
    if (isGenerandComenziFurnizori.value || !localOrderData.value?.ProiectId) return;

    const categoriiFurnizori = categoriiComenzi.value.filter(
        (c: any) => c.TipFurnizor === true && !TIPURI_STORNO_COMENZI_FURNIZORI.includes(Number(c.TipComanda))
    );
    const tipComandaFurnizor = categoriiFurnizori[0]?.TipComanda;
    if (!tipComandaFurnizor) {
        toast('Nu există nicio categorie de comandă furnizor configurată.', 'error', 4000);
        return;
    }

    const ok = await custom({
        title: 'Generare comenzi furnizori',
        messageHtml: `<div style="padding:4px 0">Se generează câte o comandă furnizor pentru fiecare furnizor de pe liniile comenzii <b>${localOrderData.value?.Cod ?? ''}</b> (furnizorul e cel setat pe fiecare linie de produs).<br>Dacă există deja comenzi furnizor generate anterior din această comandă, ele vor fi actualizate, nu duplicate.<br><br>Continui?</div>`,
        buttons: [
            { text: 'Da, generează', type: 'default', onClick: () => true },
            { text: 'Renunță', stylingMode: 'outlined', onClick: () => false },
        ],
    }).show();
    if (!ok) return;

    isGenerandComenziFurnizori.value = true;
    try {
        const resp = await ProComenziService.genereazaComenziFurnizori(props.comandaId, {
            TipComanda: tipComandaFurnizor,
            FurnizorId: null,
            DataComenzii: new Date(),
            Observatii: null,
        });
        const comenzi: any[] = resp.data ?? [];
        toast(`${comenzi.length} comandă(zi) furnizor generate/actualizate cu succes.`, 'success', 4000);
        for (const po of comenzi) {
            if (po?.Id) emit('open-order', { ...po, _isNew: false });
        }
    } catch (err: any) {
        toast(getErrMsg(err), 'error', 5000);
    } finally {
        isGenerandComenziFurnizori.value = false;
    }
}

function onGridContentReady() {
    scheduleGridInitCheck();
    liniiGrid.value = gridRef.value?.instance?.getDataSource().items() ?? [];
    if (!props.isNew || hasAutoOpenedRow.value) return;
    // content-ready se declanșează și ÎNAINTE ca încărcarea reală (detaliiStore.load()) să se
    // termine — la acel moment items() e mereu gol, indiferent dacă comanda are deja linii.
    // Fără gărzile de mai jos, redeschiderea unei comenzi "isNew" care ARE deja un produs ar
    // adăuga automat un rând nou gol, care ajunge vizibil lângă produsul real după încărcare
    // (apărea ca "produsul apare de două ori", inconsistent, în funcție de viteza răspunsului).
    if (isLoading.value || liniiGrid.value.length > 0) return;
    hasAutoOpenedRow.value = true;
    gridRef.value?.instance?.addRow();
}

// ── Apelabile din PRO_ComenziView, ÎNAINTE de a schimba tab-ul (deci înainte ca v-if
// să demonteze această comandă) — o dată demontată, editing.changes din DevExtreme
// se pierde ireversibil, deci verificarea/salvarea trebuie făcută cât timp componenta
// e încă montată, nu reactiv după aceea.
async function trySaveBeforeLeave(): Promise<{ ok: boolean }> {
    const instance = gridRef.value?.instance;
    if (!instance) return { ok: true };

    const changes = (instance.option('editing.changes') ?? []) as any[];
    if (changes.length === 0) return { ok: true };

    await onSalveazaTot();

    const remaining = (instance.option('editing.changes') ?? []) as any[];
    return { ok: remaining.length === 0 };
}

function discardUnsavedLine() {
    gridRef.value?.instance?.cancelEditData();
}

// Verificare rapidă, fără să încerce salvarea — folosită de PRO_ComenziView ca să decidă
// dacă arată dialogul de confirmare înainte de a schimba tab-ul.
function hasPendingChanges(): boolean {
    const changes = (gridRef.value?.instance?.option('editing.changes') ?? []) as any[];
    return changes.length > 0;
}

defineExpose({ hasUnsavedChanges, trySaveBeforeLeave, discardUnsavedLine, hasPendingChanges });
</script>

<style scoped>
/* ── Layout ── */
.detalii-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: #f8f9fc;
    overflow: hidden;
}

.detalii-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    border-radius: 0;
}

/* ── Header Card ── */
.order-header-card {
    flex-shrink: 0;
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 8px 12px 6px;
}

.order-header-top {
    padding: 4px 14px;
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.order-header-main {
    display: flex;
    align-items: center;
    gap: 6px;
}

.header-icon {
    font-size: 13px;
    color: #fff;
}

.order-header-texts {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
}

.order-cod {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.3px;
    line-height: 1.1;
}

.order-subtitle {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.3;
}

/* ── State actions ── */
.header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

:deep(.header-actions .dx-button) {
    color: #fff !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
}

:deep(.header-actions .dx-button.dx-button-has-text .dx-button-content) {
    padding: 1px 7px;
    font-size: 11px;
}

:deep(.header-actions .dx-button) {
    min-height: unset !important;
    height: 20px !important;
}

:deep(.header-actions .dx-button .dx-icon) {
    font-size: 12px;
    width: 12px;
    height: 12px;
}

/* ── Meta strip ── */
.order-meta {
    display: flex;
    align-items: center;
    padding: 6px 16px;
    gap: 0;
    flex-wrap: nowrap;
    border-top: 1px solid #f1f5f9;
    overflow: hidden;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px 0 0;
}

.meta-divider {
    width: 1px;
    height: 22px;
    background: #e5e7eb;
    margin-right: 12px;
    flex-shrink: 0;
}

.meta-icon {
    font-size: 12px;
    color: #64748b;
}

.meta-content {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.meta-label {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
}

.meta-value {
    font-size: 12px;
    font-weight: 500;
    color: #334155;
    line-height: 1.2;
}

.meta-item--partener,
.meta-item--obs {
    flex-shrink: 1;
    min-width: 0;
    cursor: default;
}

.meta-item--partener { max-width: 200px; }
.meta-item--obs      { max-width: 220px; }

.meta-value--partener,
.meta-value--obs {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}

.meta-value--partener { max-width: 180px; }
.meta-value--obs      { max-width: 200px; }

/* ── Tooltip generic meta strip ── */
.meta-tooltip-popup {
    position: fixed;
    z-index: 9999;
    background: #1e293b;
    color: #f8fafc;
    padding: 10px 14px;
    border-radius: 10px;
    max-width: 400px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
    pointer-events: none;
    border: 1px solid rgba(255, 255, 255, 0.07);
}

.meta-tooltip-label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    margin-bottom: 5px;
}

.meta-tooltip-text {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: #f1f5f9;
    white-space: pre-wrap;
    word-break: break-word;
}

.meta-sub {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.2;
}

.meta-spacer {
    flex: 1;
}

.total-value {
    color: #059669;
    font-weight: 700;
}

/* ── Summary zone (totals + cursuri) ── */
.meta-summary-zone {
    display: flex;
    align-items: center;
    gap: 0;
}

.meta-cursuri-box {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 5px 10px;
    background: linear-gradient(135deg, #eff6ff, #eef2ff);
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    min-width: 90px;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.08);
}

.cursuri-rows {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.curs-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
}

.curs-simbol {
    font-size: 10px;
    font-weight: 700;
    color: #2563eb;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
}

.curs-valoare {
    font-size: 13px;
    font-weight: 600;
    color: #1e40af;
    white-space: nowrap;
}

.meta-totals-box {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 6px 12px;
    background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    min-width: 160px;
    box-shadow: 0 1px 3px rgba(5, 150, 105, 0.08);
}

.totals-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
}

.totals-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    line-height: 1;
}

.totals-label--main {
    color: #065f46;
}

.totals-separator {
    height: 1px;
    background: #bbf7d0;
    margin: 1px 0;
}

.totals-value-secondary {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}

.totals-value-main {
    font-size: 14px;
    font-weight: 700;
    color: #059669;
    white-space: nowrap;
}

.facturat-chip {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #ecfdf5;
    color: #065f46;
    display: inline-block;
    white-space: nowrap;
}

.dropship-chip {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #eff6ff;
    color: #1d4ed8;
    display: inline-block;
    white-space: nowrap;
}

.po-chip {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f1f5f9;
    color: #475569;
    display: inline-block;
    white-space: nowrap;
}

.doc-anulat-chip {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #fef2f2;
    color: #991b1b;
    display: inline-block;
    white-space: nowrap;
}

.doc-stornat-chip {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #fef3c7;
    color: #92400e;
    display: inline-block;
    white-space: nowrap;
}

/* ── Popup Editare Header ── */
:deep(.dx-popup-content) {
    padding: 0 !important;
}

.ehp-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.ehp-header {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    color: #fff;
}

.ehp-header-main {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ehp-icon {
    font-size: 20px;
}

.ehp-texts {
    display: flex;
    flex-direction: column;
}

.ehp-title {
    font-size: 15px;
    font-weight: 700;
}

.ehp-subtitle {
    font-size: 12px;
    opacity: 0.85;
}

.ehp-close {
    font-size: 22px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    line-height: 1;
    padding: 0 2px;
    transition: color 0.15s;
}

.ehp-close:hover {
    color: #fff;
}

.denumiri-body {
    flex: 1;
    min-height: 0;
    padding: 12px 14px;
    overflow: hidden;
}

.ehp-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.form-group-card {
    border: 1px solid #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    background: white;
}

.form-group-caption {
    background: #f8f9fa;
    color: #333;
    font-size: 14px;
    font-weight: 600;
    padding: 8px 14px;
    border-left: 3px solid #ff2c21;
}

.form-group-content {
    background: white;
    padding: 10px 14px;
}

.fg {
    display: grid;
    gap: 8px 12px;
}

.g2 { grid-template-columns: repeat(2, 1fr); }
.g3 { grid-template-columns: repeat(3, 1fr); }

.field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.field label {
    font-weight: 500;
    color: #333;
    font-size: 13px;
}

.span2 { grid-column: span 2; }
.span3 { grid-column: span 3; }

.field-switch {
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
}

.field-checkbox-group .checkbox-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid #e5e7eb;
    background: #f8f9fc;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.checkbox-item span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
}

.checkbox-item--active {
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    border-color: transparent;
}

.checkbox-item--active span {
    color: #fff;
}

.switch-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.switch-hint {
    font-weight: 400;
    font-size: 11px;
    color: #888;
}

.edit-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 160px;
}

.edit-field--wide {
    flex: 1;
    min-width: 200px;
}

.edit-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.edit-value {
    font-size: 14px;
    font-weight: 500;
    color: #334155;
}

.edit-actions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    padding-bottom: 1px;
}

/* ── Loading ── */
.loading-placeholder {
    flex-shrink: 0;
    padding: 12px 18px;
    margin: 12px 12px 8px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-text {
    font-size: 13px;
    color: #94a3b8;
}

/* ── Grid ── */
.detalii-grid {
    flex: 1;
    min-height: 0;
    margin: 0 12px 12px;
    border-radius: 8px;
    overflow: hidden;
}

.detalii-grid :deep(.dx-row > td) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 11px !important;
    line-height: 1.1 !important;
}

.detalii-grid :deep(.row-pret-zero > td) {
    background-color: #fef3c7 !important;
}

.detalii-grid :deep(.row-pret-zero.dx-selection > td) {
    background-color: #fde68a !important;
}

.detalii-grid :deep(.dx-header-row > td) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: 24px !important;
    font-size: 11px !important;
    line-height: 1.1 !important;
}

/* ── Info icon pe header-ele coloanelor grid — mereu vizibil, indiferent de lățimea coloanei ── */
.detalii-grid :deep(.dx-header-row > td.header-with-info) {
    position: relative;
    padding-right: 20px !important;
}

.detalii-grid :deep(.col-info-icon) {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: #94a3b8;
    cursor: help;
    z-index: 2;
    pointer-events: auto;
}

.detalii-grid :deep(.col-info-icon:hover) {
    color: #2563eb;
}

.detalii-grid :deep(.dx-toolbar .dx-button) {
    height: 22px !important;
    min-height: unset !important;
}

.detalii-grid :deep(.dx-toolbar .dx-button .dx-button-content) {
    padding: 1px 7px !important;
    font-size: 11px !important;
}

.detalii-grid :deep(.dx-toolbar .dx-button .dx-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

.detalii-grid :deep(.dx-scrollbar-hoverable .dx-scrollable-scroll) {
    opacity: 1 !important;
}

.detalii-grid :deep(.dx-scrollbar-vertical .dx-scrollable-scroll) {
    width: 10px !important;
}

.detalii-grid :deep(.dx-scrollbar-horizontal .dx-scrollable-scroll) {
    height: 10px !important;
}

.detalii-grid :deep(.dx-scrollable-scroll-content) {
    background-color: #ff6a4a !important;
    border-radius: 6px !important;
}

.detalii-grid :deep(.dx-scrollbar-vertical),
.detalii-grid :deep(.dx-scrollbar-horizontal) {
    background-color: #ffe4dc !important;
}

.detalii-grid :deep(.dx-datagrid-header-panel) {
    padding: 3px 6px !important;
}

.detalii-grid :deep(.dx-toolbar) {
    height: 32px !important;
}

.detalii-grid :deep(.dx-toolbar-items-container) {
    height: 32px !important;
}

.detalii-grid :deep(.dx-datagrid-header-panel .dx-texteditor-input) {
    height: 26px !important;
    min-height: unset !important;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

.detalii-grid :deep(.dx-datagrid-header-panel .dx-texteditor) {
    height: 26px !important;
}

.detalii-grid :deep(.dx-datagrid-filter-row td) {
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

.detalii-grid :deep(.dx-datagrid-filter-row .dx-texteditor) {
    height: 26px !important;
}

.detalii-grid :deep(.dx-datagrid-filter-row .dx-texteditor-input) {
    height: 26px !important;
    min-height: unset !important;
    padding-top: 3px !important;
    padding-bottom: 3px !important;
}

/* ── Cell templates ── */
.total-cell {
    font-weight: 700;
    color: #059669;
}

.cell-val-fara-disc {
    color: #dc2626;
    font-weight: 500;
}

.cell-val-cu-disc {
    color: #059669;
    font-weight: 500;
}

.discount-badge {
    display: inline-block;
    background: #fef3c7;
    color: #92400e;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
}

.no-discount {
    color: #94a3b8;
}

/* ── Stare chip ── */
.stare-chip {
    margin-left: 10px;
}

/* ── Rezervare celulă ── */
.rezervare-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.rezervare-text {
    font-size: 12px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
}

.rezervare-full {
    background: #ecfdf5;
    color: #065f46;
}

.rezervare-partial {
    background: #fef3c7;
    color: #92400e;
}

.rezervare-none {
    background: #f1f5f9;
    color: #94a3b8;
}

/* ── Rezervare popup ── */
.rezervare-popup-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 20px;
}

.rezervare-produs-nume {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    margin: 0 0 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f1f5f9;
}

.rezervare-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
}

/* ── Banner editare concurentă ── */
.editing-banner {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 12px 6px;
    padding: 10px 16px;
    background: #fff7ed;
    border-left: 4px solid #f97316;
    border-radius: 0 8px 8px 0;
    font-size: 13px;
    color: #9a3412;
}

.editing-banner-icon {
    font-size: 16px;
    color: #f97316;
    flex-shrink: 0;
}

/* ── Generare document popup ── */
.genereaza-doc-body {
    padding: 12px 4px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* ── Secțiune PO-uri ── */
.docs-tabs {
    flex-shrink: 0;
}

.docs-tab-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    flex-wrap: wrap;
}

.docs-tab-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px 4px 10px;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    background: #f8f9fc;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    transition: background 0.15s, border-color 0.15s;
    user-select: none;
}

.docs-tab-chip:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.docs-tab-chip.active {
    background: #fff0ed;
    border-color: #ff6a4a;
    color: #d63b1f;
}

.docs-tab-chip .dx-icon {
    font-size: 13px;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: #e2e8f0;
    color: #475569;
    border-radius: 9px;
    font-size: 10px;
    font-weight: 700;
}

.docs-tab-chip.active .tab-badge {
    background: #ff6a4a;
    color: #fff;
}

.docs-tab-actions {
    margin-left: auto;
    display: flex;
    gap: 6px;
    align-items: center;
}

.docs-tab-actions :deep(.dx-button) {
    height: 22px !important;
    min-height: unset !important;
}

.docs-tab-actions :deep(.dx-button-content) {
    padding: 1px 7px !important;
    font-size: 11px !important;
}

.docs-tab-actions :deep(.dx-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

.docs-panel-grid :deep(.dx-button) {
    width: 32px !important;
    height: 32px !important;
    min-height: unset !important;
    min-width: unset !important;
}

.docs-panel-grid :deep(.dx-button .dx-icon) {
    font-size: 18px !important;
    width: 18px !important;
    height: 18px !important;
}

.docs-tab-panel {
    max-height: 220px;
    overflow-y: auto;
    overflow-x: hidden;
    border-bottom: 1px solid #e5e7eb;
    scrollbar-width: thin;
    scrollbar-color: #ff6a4a #f1f5f9;
}

.docs-tab-panel::-webkit-scrollbar {
    width: 6px;
}

.docs-tab-panel::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.docs-tab-panel::-webkit-scrollbar-thumb {
    background: #ff6a4a;
    border-radius: 3px;
}

.docs-panel-grid {
    border-radius: 0;
}

.required-star {
    color: #ff2c21;
}

/* ── Adăugare rapidă multi-cod — layout ── */
.rapida-adaugare-popup .dx-popup-content {
    padding: 0 !important;
    overflow: hidden;
    height: 100% !important;
}

.rapida-popup {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.rapida-coduri-section {
    flex-shrink: 0;
    background: white;
}

.rapida-results-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.rapida-results-caption {
    flex-shrink: 0;
    background: #f8f9fa;
    color: #333;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 12px;
    border-left: 3px solid #ff2c21;
    border-bottom: 1px solid #e9ecef;
}

.rapida-table-scroll {
    flex: 1;
    min-height: 0;
    max-height: 420px;
    overflow-y: auto;
    overflow-x: auto;
}

.rapida-empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #94a3b8;
    background: #f8f9fc;
    gap: 4px;
}

.rapida-status {
    padding: 4px 12px 6px;
    font-size: 12px;
    color: #94a3b8;
}

/* ── Adăugare rapidă multi-cod ── */
.rapida-textarea {
    width: 100%;
    box-sizing: border-box;
    font-size: 13px;
    font-family: 'Roboto', monospace;
    border: none;
    border-bottom: 1px solid #e9ecef;
    border-radius: 0;
    padding: 10px 14px;
    resize: none;
    outline: none;
    line-height: 1.5;
    color: #334155;
    display: block;
}

.rapida-textarea:focus {
    border-bottom-color: #ff2c21;
    box-shadow: none;
}


.rapida-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.rapida-table thead th {
    position: sticky;
    top: 0;
    background: #f8f9fa;
    font-weight: 600;
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
}

.rapida-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
}

.rapida-table tbody td {
    padding: 5px 8px;
    vertical-align: middle;
}

.rapida-row-ok {
    background: #f0fdf4;
}

.rapida-row-err {
    background: #fff5f5;
}

.rapida-status-cell {
    text-align: center;
    width: 28px;
}

.rapida-row-ok .dx-icon-check {
    color: #16a34a;
    font-size: 16px;
}

.rapida-row-err .dx-icon-close {
    color: #dc2626;
    font-size: 16px;
}

.rapida-cod-cell {
    font-weight: 600;
    color: #334155;
}

.rapida-den-cell {
    color: #334155;
}

.rapida-negasit {
    color: #dc2626;
    font-style: italic;
    font-size: 12px;
}

.rapida-input-cell {
    padding: 3px 6px;
}

.rapida-badge-ok {
    display: inline-block;
    margin-left: 8px;
    background: #dcfce7;
    color: #166534;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
}

.rapida-badge-err {
    display: inline-block;
    margin-left: 6px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
}

/* ── Aviz form fields ── */
.aviz-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.aviz-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

/* ── Rezervare edit icon ── */
.edit-rezervare-btn {
    font-size: 14px;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.15s;
    padding: 2px;
}

.edit-rezervare-btn:hover {
    color: #ff2c21;
}

.edit-rezervare-btn--disabled {
    color: #cbd5e1;
    cursor: not-allowed;
}

.edit-rezervare-btn--disabled:hover {
    color: #cbd5e1;
}
</style>

<style>
/* ── Dialog confirmare rezervări (global — portal DevExtreme) ── */
.dx-dialog .dx-popup-title {
    background: linear-gradient(90deg, #ff2c21, #ff6a4a) !important;
    border-bottom: none;
    padding: 0 16px;
}

.dx-dialog .dx-popup-title .dx-toolbar-label,
.dx-dialog .dx-popup-title .dx-toolbar-label span,
.dx-dialog .dx-popup-title .dx-toolbar-label div,
.dx-dialog .dx-popup-title .dx-item-content {
    color: white !important;
    font-size: 14px !important;
    font-weight: 600 !important;
}

.dx-dialog .dx-popup-title .dx-closebutton .dx-icon {
    color: rgba(255,255,255,0.8) !important;
}

.dx-dialog .dx-dialog-message {
    font-size: 13px !important;
    color: #334155 !important;
    line-height: 1.6 !important;
    padding: 18px 20px 10px !important;
}
</style>
